import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { KTSVG } from '../../../../_metronic/helpers'
import { getMenusByRole } from '../../../../myMenus'
import type { MenuGroup, MenuItem } from '../../../../_metronic/layout/components/sidebar/sidebar-menu/types'
import {
  getUserMenusTree,
  getUserProcurementPermissions,
  putBulkAcl,
  putUserProcurementPermissionOverrides,
  type MenuTreeNode,
} from '../core/_requests'

import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {
  getManagementUsers,
  getOperatorSubRoles,
  createOperator,
  updateOperator,
  updateManagementUser,
  getUserVesselAccess,
  getAssignableVesselsForUser,
  updateUserVesselAccess,
  resetUserPassword,
  sendUserPasswordResetLink,
  getCompanyAdminList,
  getCompanyList,
  createCompanyOwner,
  getAssignableRolesForUserCreate,
  getCrewRanksForDropdown,
  type AssignableVessel,
  type VesselAccessMode,
  type CrewRankOption,
} from '../core/_requests'
import type {
  CompanyAdmin,
  Company,
  OperatorSubRole,
  AssignableRoleForUserCreate,
} from '../core/_models'
// Use the same auth source as the rest of the app
import { useAuth } from '../../auth'
import { userManagementGates } from '../core/adminPermissions'
import {
  adminListEmptyMessage,
  initialAdminListAccessState,
  resolveAdminListFetchError,
  type AdminListAccessState,
} from '../core/adminListAccess'
import { UserPermissionOverridesModal } from './UserPermissionOverridesModal'
import { RolePermissionsModal } from './RolePermissionsModal'
import { getRolesList, type RoleRecord } from '../core/rolePermissionRequests'
import {
  actorCanPickAnyCompanyGroup,
  actorUsesOwnCompanyGroupScope,
  isActorGlobalScopeOperator,
  isActorOperator,
  isActorSuperAdmin,
  isEffectiveOperatorRole,
  isEffectiveSuperintendentRole,
  resolveEffectiveBehaviorType,
  resolveEffectiveCompanyGroupId,
} from '../core/adminActorContext'
import { resolveOperatorCreateScope } from '../core/userManagementOperatorCreateScope'
import { canShowResetPasswordForTarget } from '../core/userManagementPasswordReset'
import { ENABLE_OPERATOR_SUB_ROLES_UI } from '../core/rolePermissionsUiConfig'
import { isPermanentlyHiddenPlacement } from '../../../shared/navigation/permanentlyHiddenMenus'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import OperatorVesselAccessSection from './OperatorVesselAccessSection'

// Robust role & CGA resolvers that work with your various shapes
const computeRoleId = (u: any): number => {
  if (!u) return 0
  // common shapes you use
  const fromUid =
    typeof u?.uid?.role === 'number' ? u.uid.role : u?.uid?.role?.id
  const fromTop =
    typeof u?.role === 'number' ? u.role : u?.role?.id
  const fallback = u?.roleId

  const id = fromUid ?? fromTop ?? fallback ?? 0
  const n = Number(id)
  return Number.isFinite(n) ? n : 0
}

const app_link = process.env.REACT_APP_URL;
const computeCgaId = (u: any): number | null => {
  if (!u) return null
  // Prefer explicit CGA fields, then your roleEntityId (often CGA for role 5)
  const id =
    u?.companyGroupAdminId ??
    u?.companyGroupAdmin?.id ??
    u?.cga?.id ??
    u?.cgaid?.id ??
    (u?.role?.id === 5 ? u?.roleEntityId : null)

  if (id == null) return null
  const n = Number(id)
  return Number.isFinite(n) ? n : null
}

const resolveCompanyGroupIdFromAccount = (company: any): number | null => {
  const value =
    company?.cga?.id ??
    company?.cgaid?.id ??
    company?.cgaid ??
    null
  if (value == null) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

// Try hard to find a company *username/slug* by id or from the current auth object.
// Works even if /company-group-admins API isn't available to this role.
const resolveCompanySlug = (
  companyGroupAdminId: number | null | undefined,
  companyAdmins: any[],
  currentUser: any
): string | null => {
  if (companyGroupAdminId == null) return null
  const cgaId = Number(companyGroupAdminId)

  // 1) From the loaded companyAdmins (Superadmin context)
  const hit = companyAdmins?.find?.((c: any) => Number(c?.id) === cgaId)
  const fromList =
    hit?.uid?.username ??
    hit?.username ??
    null
  if (fromList && typeof fromList === 'string') return fromList

  // 2) From currentUser shapes (Company contexts)
  //   common shapes you've used across the app
  const fromAuth =
    currentUser?.companyGroupAdmin?.uid?.username ??
    currentUser?.companyGroupAdmin?.username ??
    currentUser?.uid?.username ?? // sometimes CGA is the signed-in
    null
  if (fromAuth && typeof fromAuth === 'string') return fromAuth

  // 3) Absolute last fallback - sanitize roleEntityName/name into a slug
  const raw =
    currentUser?.roleEntityName ??
    currentUser?.companyGroupAdmin?.name ??
    currentUser?.name ??
    ''
  if (typeof raw === 'string' && raw.trim()) {
    return raw
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '') // keep safe chars
  }

  return null
}

const effectiveBehaviorOfAssignableRole = (
  r: AssignableRoleForUserCreate | undefined
): string | null => {
  if (!r) return null
  if (r.customRole || normalizeBehaviorToken(r.roleType) === 'CUSTOM') {
    return normalizeBehaviorToken(r.baseRoleType || r.effectiveBehaviorType || 'COMPANY_ADMIN')
  }
  return normalizeBehaviorToken(r.roleType || r.code || r.effectiveBehaviorType)
}

const ROLE_ID_LABELS: Record<number, string> = {
  3: 'Company Owner',
  6: 'Operator',
}

const normalizeRoleLabel = (label: string): string =>
  label.trim().toUpperCase().replace(/\s+/g, '_')

const normalizeBehaviorToken = (value: unknown): string => {
  if (typeof value !== 'string') return ''
  return value.trim().toUpperCase().replace(/^ROLE_/, '').replace(/[\s-]+/g, '_')
}

const ADD_USER_SYSTEM_ROLE_ORDER = ['OPERATOR', 'SHORE', 'SUPERINTENDENT'] as const
const ADD_USER_SYSTEM_ROLE_SET = new Set<string>(ADD_USER_SYSTEM_ROLE_ORDER)

const resolveAssignableRoleCode = (role: AssignableRoleForUserCreate | undefined): string => {
  if (!role) return ''
  if (role.customRole || normalizeBehaviorToken(role.roleType) === 'CUSTOM') return 'CUSTOM'
  return normalizeBehaviorToken(role.roleType || role.code || role.effectiveBehaviorType || '')
}

const isAddUserSupportedSystemRole = (role: AssignableRoleForUserCreate | undefined): boolean =>
  ADD_USER_SYSTEM_ROLE_SET.has(resolveAssignableRoleCode(role))

const isOperatorBehaviorValue = (...values: Array<unknown>): boolean =>
  values.some((value) => isEffectiveOperatorRole(value))

const isCustomRoleValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return normalizeBehaviorToken(value) === 'CUSTOM'
  }
  if (!value || typeof value !== 'object') {
    return false
  }
  const role = value as Record<string, unknown>
  return (
    role.customRole === true ||
    normalizeBehaviorToken(role.roleType ?? role.roleBehaviorType ?? role.roleName) === 'CUSTOM'
  )
}

const isVesselAccessManagedRole = (...values: Array<unknown>): boolean =>
  values.some(
    (value) =>
      isCustomRoleValue(value) || isEffectiveOperatorRole(value) || isEffectiveSuperintendentRole(value)
  )

const cleanRoleLabel = (role: AssignableRoleForUserCreate | undefined): string => {
  if (!role) return ''
  const raw =
    role.displayName ||
    role.roleType ||
    role.baseRoleType ||
    role.effectiveBehaviorType ||
    role.code ||
    ROLE_ID_LABELS[Number(role.id)] ||
    ''

  return String(raw)
    .replace(/^\[System\]\s*/i, '')
    .replace(/^ROLE[_\s-]*/i, '')
    .replace(/[_-]+/g, ' ')
    .trim()
}

const resolveUserManagementRoleLabel = (role: AssignableRoleForUserCreate | undefined): string => {
  const cleaned = cleanRoleLabel(role)
  const normalized = normalizeRoleLabel(cleaned)
  if (!cleaned) return ''
  if (normalized === 'OPERATOR') return 'Operator'
  if (normalized === 'COMPANY_GROUP_ADMIN') return 'Company'
  if (normalized === 'COMPANY_OWNER' || normalized === 'SHORE' || normalized === 'OWNER_APPROVER') return 'Company Owner'
  return cleaned
}

const normalizeUserManagementRoleName = (role: AssignableRoleForUserCreate | undefined): string =>
  normalizeRoleLabel(resolveUserManagementRoleLabel(role))

const isAssignableRoleHiddenInCreate = (role: AssignableRoleForUserCreate | undefined): boolean => {
  if (!role || role.assignable === false) return true
  if (role.customRole || normalizeBehaviorToken(role.roleType) === 'CUSTOM') return false
  const behavior = effectiveBehaviorOfAssignableRole(role)
  const roleCode = resolveAssignableRoleCode(role)
  return (
    behavior === 'SUPER_ADMIN' ||
    roleCode === 'COMPANY_GROUP_ADMIN' ||
    roleCode === 'COMPANY_ADMIN' ||
    behavior === 'VENDOR' ||
    behavior === 'CREW'
  )
}

const userCreationRoleOrder = (role: AssignableRoleForUserCreate): number => {
  if (role.customRole) return 100
  const code = resolveAssignableRoleCode(role)
  const index = ADD_USER_SYSTEM_ROLE_ORDER.indexOf(code as (typeof ADD_USER_SYSTEM_ROLE_ORDER)[number])
  return index >= 0 ? index : 50
}

const compareUserCreationRoles = (
  a: AssignableRoleForUserCreate,
  b: AssignableRoleForUserCreate
): number => {
  const orderDiff = userCreationRoleOrder(a) - userCreationRoleOrder(b)
  if (orderDiff !== 0) return orderDiff
  const labelA = (a.displayName || a.code || String(a.id)).toLowerCase()
  const labelB = (b.displayName || b.code || String(b.id)).toLowerCase()
  if (labelA !== labelB) return labelA.localeCompare(labelB)
  return Number(a.id) - Number(b.id)
}

const resolveAddUserRoleLabel = (role: AssignableRoleForUserCreate | undefined): string => {
  if (!role) return ''
  if (role.customRole) {
    const label = (role.displayName || role.code || `Role #${role.id}`).trim()
    return label ? `${label} (Custom)` : `Role #${role.id} (Custom)`
  }
  const code = resolveAssignableRoleCode(role)
  if (code === 'OPERATOR') return 'Operator'
  if (code === 'SHORE' || code === 'COMPANY_OWNER' || code === 'OWNER_APPROVER') return 'Company Owner'
  if (code === 'SUPERINTENDENT') return 'Superintendent'
  if (code === 'COMPANY_GROUP_ADMIN') return 'Company'
  return normalizeUserManagementRoleName(role)
}


// put near top of UserManagement.tsx (REPLACE THIS WHOLE BLOCK)
type RoleLike = { id?: number; roleName?: string } | number | null | undefined
type CurrentUserLike = {
  // shapes we saw across app/auth
  uid?: { role?: { id?: number } | number | null } | null
  role?: { id?: number; roleName?: string } | null
  roleId?: number | null
  roleEntityId?: number | null

  // CGA variants
  companyGroupAdminId?: number | null
  cga?: { id?: number | null } | null
  cgaid?: { id?: number | null } | null
  companyGroupAdmin?: { id?: number | null } | null
  companyAdminId?: number | null
}

// replace with your real selector/useAuth later
const getCurrentUser = (): CurrentUserLike | null => {
  try {
    const raw = localStorage.getItem('auth') || localStorage.getItem('currentUser')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// Robust role resolver: tries multiple shapes
const getRoleId = (u: CurrentUserLike | null): number => {
  if (!u) return 0
  // common shapes
  const fromUid = typeof (u.uid as any)?.role === 'number'
    ? (u.uid as any).role
    : (u.uid as any)?.role?.id

  const fromTop = typeof (u.role as any) === 'number'
    ? (u.role as any)
    : u.role?.id

  return Number(fromUid ?? fromTop ?? u.roleId ?? 0) || 0
}

// Robust CGA resolver: companyGroupAdminId when company; null for superadmin
const getCgaId = (u: CurrentUserLike | null): number | null => {
  if (!u) return null
  return (
    u.companyGroupAdminId ??
    u.companyGroupAdmin?.id ??
    (u as any)?.cgaid?.id ??
    (u as any)?.cga?.id ??
    null
  )
}

// Username rules:
// - 3-32 chars
// - starts with a letter
// - letters, numbers, dot, underscore, hyphen
const USERNAME_REGEX = /^[a-zA-Z][a-zA-Z0-9._-]{2,31}$/

const usernameExists = (uname: string, list: User[]) =>
  list.some(o => (o?.username || '').toLowerCase() === uname.toLowerCase())

const suggestUsername = (fullName: string, taken: Set<string>) => {
  const base = fullName.trim().toLowerCase()
    .replace(/[^a-z0-9\s.-]/g, '')
    .replace(/\s+/g, '.')
    .replace(/^\.+|\.+$/g, '')
  let u = base.replace(/^[^a-z]+/, '') || 'user'
  if (!/^[a-z]/.test(u)) u = 'u-' + u
  u = u.slice(0, 16)
  let n = 0, candidate = u
  while (taken.has(candidate)) {
    n += 1
    candidate = `${u}${n}`
    if (candidate.length > 32) candidate = candidate.slice(0, 32)
  }
  return candidate
}

// Password validator:
// - >= 7 chars
// - includes: upper, lower, digit, special
// - no spaces
// - must not contain user's name or email local part
const validatePassword = (pwd: string, ctx: { name?: string; email?: string }) => {
  const errors: string[] = []
  if (pwd.length < 7) errors.push('At least 7 characters')
  if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter')
  if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter')
  if (!/[0-9]/.test(pwd)) errors.push('One number')
  if (!/[^\w\s]/.test(pwd)) errors.push('One special character')
  if (/\s/.test(pwd)) errors.push('No spaces')
  const local = (ctx.email || '').split('@')[0]?.toLowerCase() || ''
  const nameChunks = (ctx.name || '').toLowerCase().split(/\s+/).filter(Boolean)
  const hay = pwd.toLowerCase()
  if (local && local.length >= 3 && hay.includes(local)) errors.push('Must not include your email')
  if (nameChunks.some(n => n.length >= 3 && hay.includes(n))) errors.push('Must not include your name')
  return { ok: errors.length === 0, errors }
}

const generateTemporaryPassword = (length = 12): string => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghjkmnpqrstuvwxyz'
  const digits = '23456789'
  const special = '!@#$%&*+-=?'
  const all = upper + lower + digits + special
  const pick = (chars: string) => chars[Math.floor(Math.random() * chars.length)]
  const chars: string[] = [pick(upper), pick(lower), pick(digits), pick(special)]
  while (chars.length < length) {
    chars.push(pick(all))
  }
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
      ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}


interface User {
  id: number
  userId: number
  operatorId?: number | null
  roleId?: number
  /** Display label from API (custom display name / code or system-facing name). */
  roleName?: string
  /** Effective behavior enum name, e.g. OPERATOR, SUPER_ADMIN - from API roleType field. */
  roleBehaviorType?: string
  baseRoleType?: string | null
  name: string
  dateOfBirth: string
  email: string
  mobile: string
  username: string
  password: string
  passwordSet?: boolean
  displayPassword?: string | null
  /** Superadmin-only column (derived): */
  companyName?: string
  enabled?: boolean
  vesselAccessMode?: VesselAccessMode | null
  operatorSubRoleId?: number | null
  operatorSubRoleLabel?: string | null
}

const normalizeMirrorPassword = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.startsWith('$2a$') || trimmed.startsWith('$2b$') || trimmed.startsWith('$2y$')) {
    return null
  }
  return trimmed
}

const normalizeDisplayPassword = (raw: any): string | null =>
  normalizeMirrorPassword(raw?.displayPassword ?? raw?.display_password ?? null)

const formatRoleLabel = (role?: string) => {
  if (!role) return 'Unknown'
  const cleaned = role.replace(/^\s*\[System\]\s*/i, '').trim()
  if (!cleaned) return 'Unknown'
  const normalized = cleaned.toUpperCase().replace(/[\s_-]+/g, '_')
  if (
    normalized === 'SHORE' ||
    normalized === 'OWNER_APPROVER' ||
    normalized === 'COMPANY_OWNER'
  ) {
    return 'Company Owner'
  }
  if (normalized === 'COMPANY_GROUP_ADMIN') return 'Company'
  return cleaned
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .map((part) => {
      const lower = part.toLowerCase()
      return lower.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join(' ')
}

const normalizeVesselAccessMode = (value: unknown): VesselAccessMode | null => {
  if (value !== 'ALL' && value !== 'ASSIGNED' && value !== 'NONE') {
    return null
  }
  return value
}

function formatRoleCatalogFilterLabel(r: RoleRecord): string {
  const primary = ((r.displayName || r.code || '').trim()) || ''
  const behRaw = (r.effectiveBehaviorType || r.baseRoleType || r.roleType || '').toString().trim()
  if (r.customRole) {
    const baseLabel = primary || behRaw || `Role #${r.id}`
    const behLabel = behRaw ? formatRoleLabel(behRaw) : ''
    return behLabel ? `${baseLabel} (Custom / ${behLabel})` : `${baseLabel} (Custom)`
  }
  if (primary) return formatRoleLabel(primary)
  return formatRoleLabel(behRaw || 'Unknown')
}


interface NewUser {
  name: string
  dateOfBirth: string
  email: string
  mobile: string
  username: string
  password: string
  confirmPassword: string
}

interface MenuStructure {
  [key: string]: {
    menus: string[]
    submenus: {
      [key: string]: string[]
    }
  }
}

interface PermissionSet {
  view: boolean
  add: boolean
  edit: boolean
  delete: boolean
  approve: boolean
  download: boolean
}

interface Permissions {
  [key: string]: PermissionSet
}

interface SearchResult {
  type: 'module' | 'menu' | 'submenu'
  moduleName: string
  menuName: string | null
  submenuName: string | null
  key: string
}

type ApiLevel = 'MODULE' | 'MENU' | 'SUBMENU'
interface ApiMenuNode {
  level: ApiLevel
  id: number
  name: string
  sortOrder: number | null
  canView: boolean
  canAdd: boolean
  canEdit: boolean
  canDelete: boolean
  children: ApiMenuNode[]
}

type ProcurementActionKey = keyof PermissionSet

const normalizePermissionLabel = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '')

const PROCUREMENT_ROW_ALIASES: Record<string, string> = {
  rfq: 'rfqmanagement',
  quotations: 'quotationmanagement',
  purchaseorder: 'purchaseorders',
  purchaseordermanagement: 'purchaseorders',
  deliveries: 'deliverytracking',
  goodsreceipts: 'goodsreceipt',
  invoicematching: 'invoicemanagement',
  invoicemanagement: 'invoicemanagement',
  procurementlogs: 'audittrail',
  procurementaudittrail: 'audittrail',
  procurementusermanagement: 'usermanagement',
  requisitionlibrary: 'requisitionlibraries',
  libraries: 'requisitionlibraries',
}

const PROCUREMENT_ROW_ACTION_PERMISSION_CODES: Record<
  string,
  Partial<Record<ProcurementActionKey, string[]>>
> = {
  requisitions: {
    view: ['READ_REQUISITION'],
    add: ['CREATE_REQUISITION'],
    edit: ['UPDATE_REQUISITION', 'REQUISITION_ADD_ITEMS'],
    delete: ['DELETE_REQUISITION'],
    approve: ['REQUISITION_APPROVE_VESSEL', 'REQUISITION_APPROVE_SHORE'],
  },
  rfqmanagement: {
    view: ['RFQ_VIEW'],
    add: ['RFQ_CREATE'],
    edit: ['RFQ_EDIT'],
    approve: ['RFQ_SEND'],
  },
  quotationmanagement: {
    view: ['QUOTATION_VIEW'],
    add: ['QUOTATION_SELECT'],
    edit: ['QUOTATION_REWORK'],
    approve: ['QUOTATION_COMPARE', 'QUOTATION_OWNER_APPROVE', 'QUOTATION_OWNER_REJECT'],
  },
  purchaseorders: {
    view: ['READ_PURCHASE_ORDER'],
    add: ['CREATE_PURCHASE_ORDER'],
    edit: ['UPDATE_PURCHASE_ORDER'],
    approve: ['PO_SEND', 'PO_OWNER_APPROVE', 'PO_OWNER_REJECT'],
  },
  deliverytracking: {
    view: ['DELIVERY_VIEW'],
    add: ['DELIVERY_CREATE'],
    edit: ['DELIVERY_CREATE'],
  },
  goodsreceipt: {
    view: ['READ_GOODS_RECEIPT'],
    add: ['CREATE_GOODS_RECEIPT'],
    edit: ['CREATE_GOODS_RECEIPT'],
  },
  invoicemanagement: {
    view: ['INVOICE_VIEW'],
    add: ['INVOICE_CREATE'],
    edit: ['INVOICE_CREATE'],
  },
  requisitionlibraries: {
    view: [
      'READ_LIBRARY_CATEGORY',
      'LIBRARY_HEAD_VIEW',
      'LIBRARY_SUBHEAD_VIEW',
      'LIBRARY_SUBCATEGORY_VIEW',
      'LIBRARY_ACCOUNTING_VIEW',
      'LIBRARY_SUBACCOUNTING_VIEW',
      'LIBRARY_ADDRESS_VIEW',
    ],
    add: [
      'CREATE_LIBRARY_CATEGORY',
      'LIBRARY_HEAD_MANAGE',
      'LIBRARY_SUBHEAD_MANAGE',
      'LIBRARY_SUBCATEGORY_MANAGE',
      'LIBRARY_ACCOUNTING_MANAGE',
      'LIBRARY_SUBACCOUNTING_MANAGE',
      'LIBRARY_ADDRESS_MANAGE',
    ],
    edit: [
      'UPDATE_LIBRARY_CATEGORY',
      'LIBRARY_HEAD_MANAGE',
      'LIBRARY_SUBHEAD_MANAGE',
      'LIBRARY_SUBCATEGORY_MANAGE',
      'LIBRARY_ACCOUNTING_MANAGE',
      'LIBRARY_SUBACCOUNTING_MANAGE',
      'LIBRARY_ADDRESS_MANAGE',
    ],
    delete: [
      'DELETE_LIBRARY_CATEGORY',
      'LIBRARY_HEAD_MANAGE',
      'LIBRARY_SUBHEAD_MANAGE',
      'LIBRARY_SUBCATEGORY_MANAGE',
      'LIBRARY_ACCOUNTING_MANAGE',
      'LIBRARY_SUBACCOUNTING_MANAGE',
      'LIBRARY_ADDRESS_MANAGE',
    ],
    approve: [
      'CREATE_LIBRARY_CATEGORY',
      'UPDATE_LIBRARY_CATEGORY',
      'DELETE_LIBRARY_CATEGORY',
      'LIBRARY_HEAD_MANAGE',
      'LIBRARY_SUBHEAD_MANAGE',
      'LIBRARY_SUBCATEGORY_MANAGE',
      'LIBRARY_ACCOUNTING_MANAGE',
      'LIBRARY_SUBACCOUNTING_MANAGE',
      'LIBRARY_ADDRESS_MANAGE',
    ],
  },
  categorylibrary: {
    view: ['READ_LIBRARY_CATEGORY', 'READ_REQUISITION_CATEGORY_LIBRARY'],
    add: ['CREATE_LIBRARY_CATEGORY', 'CREATE_REQUISITION_CATEGORY_LIBRARY'],
    edit: ['UPDATE_LIBRARY_CATEGORY', 'UPDATE_REQUISITION_CATEGORY_LIBRARY'],
    delete: ['DELETE_LIBRARY_CATEGORY', 'DELETE_REQUISITION_CATEGORY_LIBRARY'],
    approve: ['CREATE_LIBRARY_CATEGORY', 'UPDATE_LIBRARY_CATEGORY', 'DELETE_LIBRARY_CATEGORY'],
  },
  headlibrary: {
    view: ['LIBRARY_HEAD_VIEW'],
    add: ['LIBRARY_HEAD_MANAGE'],
    edit: ['LIBRARY_HEAD_MANAGE'],
    delete: ['LIBRARY_HEAD_MANAGE'],
    approve: ['LIBRARY_HEAD_MANAGE'],
  },
  subheadlibrary: {
    view: ['LIBRARY_SUBHEAD_VIEW'],
    add: ['LIBRARY_SUBHEAD_MANAGE'],
    edit: ['LIBRARY_SUBHEAD_MANAGE'],
    delete: ['LIBRARY_SUBHEAD_MANAGE'],
    approve: ['LIBRARY_SUBHEAD_MANAGE'],
  },
  subcategorylibrary: {
    view: ['LIBRARY_SUBCATEGORY_VIEW'],
    add: ['LIBRARY_SUBCATEGORY_MANAGE'],
    edit: ['LIBRARY_SUBCATEGORY_MANAGE'],
    delete: ['LIBRARY_SUBCATEGORY_MANAGE'],
    approve: ['LIBRARY_SUBCATEGORY_MANAGE'],
  },
  accountingcodelibrary: {
    view: ['LIBRARY_ACCOUNTING_VIEW'],
    add: ['LIBRARY_ACCOUNTING_MANAGE'],
    edit: ['LIBRARY_ACCOUNTING_MANAGE'],
    delete: ['LIBRARY_ACCOUNTING_MANAGE'],
    approve: ['LIBRARY_ACCOUNTING_MANAGE'],
  },
  subaccountinglibrary: {
    view: ['LIBRARY_SUBACCOUNTING_VIEW'],
    add: ['LIBRARY_SUBACCOUNTING_MANAGE'],
    edit: ['LIBRARY_SUBACCOUNTING_MANAGE'],
    delete: ['LIBRARY_SUBACCOUNTING_MANAGE'],
    approve: ['LIBRARY_SUBACCOUNTING_MANAGE'],
  },
  addresslibrary: {
    view: ['LIBRARY_ADDRESS_VIEW'],
    add: ['LIBRARY_ADDRESS_MANAGE'],
    edit: ['LIBRARY_ADDRESS_MANAGE'],
    delete: ['LIBRARY_ADDRESS_MANAGE'],
    approve: ['LIBRARY_ADDRESS_MANAGE'],
  },
}

const resolveProcurementRowFromPermissionKey = (key: string): string | null => {
  if (!key) return null
  const parts = key.split('-').map((part) => part.trim()).filter(Boolean)
  if (parts.length < 2) return null

  let rowLabel: string | null = null
  if (parts.length >= 3 && normalizePermissionLabel(parts[1]) === 'procurement') {
    rowLabel = parts.slice(2).join('-')
  } else if (normalizePermissionLabel(parts[0]) === 'procurement') {
    rowLabel = parts.slice(1).join('-')
  }

  if (!rowLabel) return null
  const normalized = normalizePermissionLabel(rowLabel)
  return PROCUREMENT_ROW_ALIASES[normalized] ?? normalized
}

const isProcurementPermissionKey = (key: string): boolean =>
  Boolean(resolveProcurementRowFromPermissionKey(key))

const resolveProcurementActionCodes = (
  key: string,
  action: ProcurementActionKey
): string[] => {
  const row = resolveProcurementRowFromPermissionKey(key)
  if (!row) return []
  const actionMap = PROCUREMENT_ROW_ACTION_PERMISSION_CODES[row]
  if (!actionMap) return []
  return actionMap[action] ?? []
}

const overlayProcurementSemanticPermissions = (
  basePermissions: Permissions,
  semanticPermissions: Record<string, { allowed: boolean }>
): Permissions => {
  const merged: Permissions = { ...basePermissions }
    ; (Object.keys(merged) as string[]).forEach((key) => {
      if (!isProcurementPermissionKey(key)) return

      const current = merged[key] ?? {
        view: false,
        add: false,
        edit: false,
        delete: false,
        approve: false,
        download: false,
      }

      const resolve = (action: ProcurementActionKey): boolean => {
        const codes = resolveProcurementActionCodes(key, action)
        if (!codes.length) return current[action] ?? false
        return codes.some((code) => semanticPermissions[code]?.allowed === true)
      }

      merged[key] = {
        ...current,
        view: resolve('view'),
        add: resolve('add'),
        edit: resolve('edit'),
        delete: resolve('delete'),
        approve: resolve('approve'),
        download: resolve('download'),
      }
    })
  return merged
}

const collectProcurementSemanticDecisions = (
  permissionMap: Permissions
): Record<string, boolean> => {
  const decisions: Record<string, boolean> = {}
    ; (Object.entries(permissionMap) as Array<[string, PermissionSet]>).forEach(([key, value]) => {
      if (!isProcurementPermissionKey(key)) return
        ; (['view', 'add', 'edit', 'delete', 'approve', 'download'] as ProcurementActionKey[]).forEach((action) => {
          const codes = resolveProcurementActionCodes(key, action)
          if (!codes.length) return
          const actionAllowed = Boolean(value?.[action])
          codes.forEach((code) => {
            if (!(code in decisions)) decisions[code] = false
            if (actionAllowed) decisions[code] = true
          })
        })
    })
  return decisions
}

function assignableRoleTypeMeta(r: AssignableRoleForUserCreate): { label: string; badgeClass: string } {
  if (r.systemRole || r.protectedRole) {
    return { label: 'System', badgeClass: 'badge badge-light-warning text-gray-800' }
  }
  if (r.customRole) {
    if (r.companyGroupAdminId != null) {
      return { label: 'Company custom', badgeClass: 'badge badge-light-primary text-gray-800' }
    }
    return { label: 'Global custom', badgeClass: 'badge badge-light-info text-gray-800' }
  }
  return { label: 'Global', badgeClass: 'badge badge-light text-gray-800' }
}

const UserManagement = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentUser, hasPermission, hasAnyPermission, isSuperAdmin, auth, roleDetails, tenant } = useAuth()
  const actorOpts = React.useMemo(
    () => ({ auth, roleDetails, tenant }),
    [auth, roleDetails, tenant]
  )
  const actorEffectiveBehavior = React.useMemo(
    () => resolveEffectiveBehaviorType(currentUser, actorOpts),
    [currentUser, actorOpts]
  )
  const actorIsSuperAdmin = React.useMemo(
    () => isActorSuperAdmin(currentUser, actorOpts),
    [currentUser, actorOpts]
  )
  const actorIsGlobalOperator = React.useMemo(
    () => isActorGlobalScopeOperator(currentUser, actorOpts),
    [currentUser, actorOpts]
  )
  const actorIsScopedOperator = React.useMemo(
    () => isActorOperator(currentUser, actorOpts) && !actorIsGlobalOperator,
    [currentUser, actorOpts, actorIsGlobalOperator]
  )
  const actorCanPickCompanyGroup = React.useMemo(
    () => actorCanPickAnyCompanyGroup(currentUser, actorOpts),
    [currentUser, actorOpts]
  )
  const actorOwnCompanyGroupScope = React.useMemo(
    () => actorUsesOwnCompanyGroupScope(currentUser, actorOpts),
    [currentUser, actorOpts]
  )
  const actionGates = React.useMemo(
    () => userManagementGates(hasPermission, hasAnyPermission, isSuperAdmin, actorEffectiveBehavior),
    [actorEffectiveBehavior, hasAnyPermission, hasPermission, isSuperAdmin]
  )
  const canExportUsers = isSuperAdmin || hasPermission('EXPORT_USER')
  const myCompanyGroupAdminId = React.useMemo(
    () => resolveEffectiveCompanyGroupId(currentUser),
    [currentUser]
  )
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [listAccess, setListAccess] = useState<AdminListAccessState>(initialAdminListAccessState)

  // We need to disable "User Permissions" button for self
  // roleEntityId in your payload matches the "user.id" (what we later map into row.userId)
  // We need to disable "User Permissions" button for self
  // roleEntityId == operatorId (for operators)
  // also try to resolve your "app user id" for safety
  const myOperatorId = React.useMemo(() => {
    const v = (currentUser as any)?.roleEntityId
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }, [currentUser])

  const myAppUserId = React.useMemo(() => {
    // try common shapes seen across your auth
    const v =
      (currentUser as any)?.user?.id ??
      (currentUser as any)?.uid?.id ??
      (currentUser as any)?.id ??
      null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }, [currentUser])

  // Only OPERATORS are blocked from opening their own permissions.
  // Superadmin (and others) can open any operator.
  const isSelfRow = React.useCallback((row: User) => {
    if (!isActorOperator(currentUser)) return false // self-guard only for operators

    const rowOperatorId = row.operatorId != null ? Number(row.operatorId) : null
    const rowUserId = Number(row.userId)

    const opMatch =
      myOperatorId != null &&
      Number.isFinite(rowOperatorId) &&
      rowOperatorId === myOperatorId

    const userMatch =
      myAppUserId != null &&
      rowUserId != null &&
      rowUserId === myAppUserId

    return !!(opMatch || userMatch)
  }, [currentUser, myOperatorId, myAppUserId])

  const canShowRowResetPassword = React.useCallback(
    (row: User) =>
      canShowResetPasswordForTarget(currentUser as Record<string, unknown>, row, {
        canResetPassword: actionGates.canResetPassword,
        actorAppUserId: myAppUserId,
        actorOpts,
      }),
    [actionGates.canResetPassword, actorOpts, currentUser, myAppUserId]
  )




  const [showAddUserForm, setShowAddUserForm] = useState(false)
  const [showCredentialsModal, setShowCredentialsModal] = useState(false)
  const [visiblePasswords, setVisiblePasswords] = useState<Set<number>>(new Set())
  type NewCreds = { username: string; password: string; loginUrl: string }
  const [newCredentials, setNewCredentials] = useState<NewCreds | null>(null)
  const [pendingOperatorAccessUserId, setPendingOperatorAccessUserId] = useState<number | null>(null)
  const [copied, setCopied] = useState(false)
  const [copiedPasswords, setCopiedPasswords] = useState<Set<number>>(new Set())
  const [permissionOverrideUser, setPermissionOverrideUser] = useState<User | null>(null)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    mobile: '',
    dateOfBirth: '',
    enabled: true,
    operatorSubRoleId: '' as string | number,
  })
  const [savingEdit, setSavingEdit] = useState(false)
  const [editVesselAccessLoading, setEditVesselAccessLoading] = useState(false)
  const [editVesselAccessSaving, setEditVesselAccessSaving] = useState(false)
  const [editVesselAccessError, setEditVesselAccessError] = useState('')
  const [editVesselAccessMode, setEditVesselAccessMode] = useState<VesselAccessMode>('NONE')
  const [editAssignedVesselIds, setEditAssignedVesselIds] = useState<Set<number>>(new Set())
  const [editAssignableVessels, setEditAssignableVessels] = useState<AssignableVessel[]>([])
  const [editAssignableVesselsLoading, setEditAssignableVesselsLoading] = useState(false)
  const [editAssignableVesselsError, setEditAssignableVesselsError] = useState('')
  const [resetUser, setResetUser] = useState<User | null>(null)
  const [resetForm, setResetForm] = useState({ newPassword: '', confirmPassword: '' })
  const [resettingPassword, setResettingPassword] = useState(false)
  const [resetSuccessPassword, setResetSuccessPassword] = useState<string | null>(null)
  const [resetPasswordCopied, setResetPasswordCopied] = useState(false)
  const [showResetPwd, setShowResetPwd] = useState(false)
  const [rolePermissionsModalOpen, setRolePermissionsModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof User | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' })

  const [newUser, setNewUser] = useState<NewUser>({
    name: '',
    dateOfBirth: '',
    email: '',
    mobile: '',
    username: '',
    password: '',
    confirmPassword: '',
  })

  const [roleFilter, setRoleFilter] = useState<string>('ALL')
  const [roleFilterCatalog, setRoleFilterCatalog] = useState<RoleRecord[]>([])
  const [roleFilterCatalogLoading, setRoleFilterCatalogLoading] = useState(false)

  // Permissions Modal State
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedMenu, setSelectedMenu] = useState('');
  const [selectedSubmenu, setSelectedSubmenu] = useState('');
  const [permissions, setPermissions] = useState<Permissions>({});
  const [selectedMenus, setSelectedMenus] = useState<Set<string>>(new Set());
  const [selectedSubmenus, setSelectedSubmenus] = useState<Set<string>>(new Set());
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const [filteredResults, setFilteredResults] = useState<SearchResult[]>([]);
  const tableRef = useRef<HTMLDivElement>(null);

  // Dynamic menuStructure from API (with static fallback)
  const [menuStructure, setMenuStructure] = useState<MenuStructure>({})
  const [menuLoading, setMenuLoading] = useState<boolean>(false)
  const [menuError, setMenuError] = useState<string>('')


  const [creating, setCreating] = useState(false)
  const operatorSubRolesUiEnabled = ENABLE_OPERATOR_SUB_ROLES_UI

  const showCompanyCol = actorCanPickCompanyGroup

  const [showPwd, setShowPwd] = useState(false)
  const [showConfirmPwd, setShowConfirmPwd] = useState(false)
  const [assignableRolesForCreate, setAssignableRolesForCreate] = useState<AssignableRoleForUserCreate[]>([])
  const [assignableRolesLoading, setAssignableRolesLoading] = useState(false)
  const [assignableRolesError, setAssignableRolesError] = useState('')
  const [addUserRoleId, setAddUserRoleId] = useState<number | ''>('')

  const selectedRoleForAdd = useMemo(
    () => assignableRolesForCreate.find((r) => r.id === addUserRoleId),
    [assignableRolesForCreate, addUserRoleId]
  )
  const addUserEffectiveBehavior = useMemo(
    () => effectiveBehaviorOfAssignableRole(selectedRoleForAdd),
    [selectedRoleForAdd]
  )
  const creatableAssignableRolesForAddUser = useMemo(
    () =>
      assignableRolesForCreate
        .filter((r) => !isAssignableRoleHiddenInCreate(r))
        .filter((r) => isAddUserSupportedSystemRole(r) || r.customRole === true)
        .sort(compareUserCreationRoles),
    [assignableRolesForCreate]
  )
  const selectedAddUserRoleIsValid = useMemo(
    () => addUserRoleId !== '' && creatableAssignableRolesForAddUser.some((role) => role.id === addUserRoleId),
    [addUserRoleId, creatableAssignableRolesForAddUser]
  )
  const ownerMode = ['SHORE', 'COMPANY_OWNER', 'OWNER_APPROVER'].includes(addUserEffectiveBehavior || '')
  const operatorCreateMode = addUserEffectiveBehavior === 'OPERATOR'
  const vesselAccessCreateMode = isVesselAccessManagedRole(selectedRoleForAdd)
  const crewCreateMode = addUserEffectiveBehavior === 'CREW'

  const selectedRoleSummaryLine = useMemo(() => {
    const r = selectedRoleForAdd
    if (!r) return null
    const d = (r.description || '').trim()
    if (d) return d
    const bits: string[] = []
    if (r.defaultDataScope) bits.push(`Company scope: ${r.defaultDataScope}`)
    const beh = (r.baseRoleType || r.effectiveBehaviorType || r.roleType || '').replace(/_/g, ' ')
    if (beh) bits.push(`Access behavior: ${beh}`)
    return bits.length > 0 ? bits.join(' / ') : 'Standard role from your catalog.'
  }, [selectedRoleForAdd])

  const [operatorSubRoles, setOperatorSubRoles] = useState<OperatorSubRole[]>([])
  const [operatorSubRolesLoading, setOperatorSubRolesLoading] = useState(false)
  const [newOperatorSubRoleId, setNewOperatorSubRoleId] = useState<number | ''>('')
  const [crewRanks, setCrewRanks] = useState<CrewRankOption[]>([])
  const [crewRanksLoading, setCrewRanksLoading] = useState(false)
  const [newCrewRankId, setNewCrewRankId] = useState<number | ''>('')
  const [updatingOperatorSubRoleUserId, setUpdatingOperatorSubRoleUserId] = useState<number | null>(
    null
  )

  // SUPERADMIN helpers
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [companyAdminAccounts, setCompanyAdminAccounts] = useState<Company[]>([])
  type AssignTarget = 'superadmin' | 'company'
  const [assignTarget, setAssignTarget] = useState<AssignTarget>('superadmin') // only used by roleId===1
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null)
  const [selectedOwnerCompanyAdminId, setSelectedOwnerCompanyAdminId] = useState<number | null>(null)

  // Maps "Module-Menu" / "Module-Menu-Submenu" -> ids needed by API
  const [keyIdMap, setKeyIdMap] = useState<Record<string, {
    moduleId: number
    menuId: number | null
    submenuId: number | null
    subsubmenuId: number | null
  }>>({})

  const editVesselAccessRequestSeq = useRef(0)
  const editAssignableVesselsRequestSeq = useRef(0)


  // For diffing: the permission flags when we fetched the tree
  const [baselinePermissions, setBaselinePermissions] = useState<Permissions>({})
  const [treeBaselinePermissions, setTreeBaselinePermissions] = useState<Permissions>({})
  const [procurementBaselineByCode, setProcurementBaselineByCode] = useState<Record<string, boolean>>({})

  const takenUsernames = useMemo(
    () => new Set(users.map(o => (o.username || '').toLowerCase())),
    [users]
  )

  const sortedRoleFilterCatalog = useMemo(() => {
    const list = [...roleFilterCatalog]
    list.sort((a, b) => {
      const ac = a.customRole ? 1 : 0
      const bc = b.customRole ? 1 : 0
      if (ac !== bc) return ac - bc
      const an = (a.displayName || a.code || String(a.id)).toLowerCase()
      const bn = (b.displayName || b.code || String(b.id)).toLowerCase()
      return an.localeCompare(bn)
    })
    return list
  }, [roleFilterCatalog])

  const filteredOwnerCompanyAdmins = useMemo(() => {
    const source = companyAdminAccounts || []

    if (actorCanPickCompanyGroup) {
      if (((assignTarget === 'company') || ownerMode) && selectedCompanyId != null) {
        return source.filter((company) => {
          const companyGroupId = resolveCompanyGroupIdFromAccount(company)
          return companyGroupId != null && companyGroupId === Number(selectedCompanyId)
        })
      }
      if (ownerMode) return []
      return source
    }

    if (actorOwnCompanyGroupScope) {
      return source.filter((company) => {
        const companyGroupId = resolveCompanyGroupIdFromAccount(company)
        return companyGroupId != null && myCompanyGroupAdminId != null
          ? companyGroupId === Number(myCompanyGroupAdminId)
          : false
      })
    }

    return []
  }, [
    companyAdminAccounts,
    actorCanPickCompanyGroup,
    actorOwnCompanyGroupScope,
    assignTarget,
    selectedCompanyId,
    myCompanyGroupAdminId,
    ownerMode,
  ])

  useEffect(() => {
    if (addUserEffectiveBehavior !== 'OPERATOR') {
      setNewOperatorSubRoleId('')
    }
    if (addUserEffectiveBehavior !== 'CREW') {
      setNewCrewRankId('')
    }
  }, [addUserEffectiveBehavior])

  useEffect(() => {
    if (!operatorSubRolesUiEnabled || !(actorCanPickCompanyGroup || actorOwnCompanyGroupScope)) {
      setOperatorSubRoles([])
      return
    }
    let cancelled = false
    setOperatorSubRolesLoading(true)
    getOperatorSubRoles()
      .then((list) => {
        if (!cancelled) {
          setOperatorSubRoles((list || []).filter((sr) => sr.active !== false))
        }
      })
      .catch((e: any) => {
        if (!cancelled) {
          if (operatorSubRolesUiEnabled) {
            toast.error('Failed to load operator sub-roles', { position: 'top-center' })
          } else if (process.env.NODE_ENV !== 'production') {
            console.warn('Suppressed operator sub-role preload error:', e)
          }
          setOperatorSubRoles([])
        }
      })
      .finally(() => {
        if (!cancelled) setOperatorSubRolesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [actorCanPickCompanyGroup, actorOwnCompanyGroupScope, operatorSubRolesUiEnabled])

  useEffect(() => {
    if (!(actorCanPickCompanyGroup || actorOwnCompanyGroupScope)) {
      setCrewRanks([])
      return
    }
    let cancelled = false
    setCrewRanksLoading(true)
    getCrewRanksForDropdown()
      .then((list) => {
        if (!cancelled) setCrewRanks(Array.isArray(list) ? list : [])
      })
      .catch((e: any) => {
        if (!cancelled) {
          toast.error('Failed to load crew ranks', { position: 'top-center' })
          setCrewRanks([])
        }
      })
      .finally(() => {
        if (!cancelled) setCrewRanksLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [actorCanPickCompanyGroup, actorOwnCompanyGroupScope])

  const loadUsers = async () => {
    if (!actionGates.canReadList) {
      setListAccess({ loading: false, accessDenied: true, loadError: null })
      setUsers([])
      return
    }
    setLoading(true)
    setListAccess((s) => ({ ...s, loading: true, accessDenied: false, loadError: null }))
    try {
      const data = await getManagementUsers()

      // Map management users -> UI row
      const mapped: User[] = data.map((u) => {
        const displayPassword = normalizeDisplayPassword(u)
        const companyLabel =
          u.companyName && u.companyName.trim()
            ? u.companyName
            : u.roleType === 'SUPER_ADMIN'
              ? 'Superadmin'
              : undefined

        return {
          id: u.userId,
          userId: u.userId,
          operatorId: u.operatorId ?? null,
          roleId: u.roleId,
          roleName: u.roleName ?? '',
          roleBehaviorType: (u.roleType ?? '').toUpperCase(),
          baseRoleType: u.baseRoleType ?? null,
          name: u.fullName || u.username || 'Unknown',
          dateOfBirth: u.dateOfBirth ?? '',
          email: u.email ?? '',
          mobile: u.mobile ?? '',
          username: u.username ?? '',
          password: '',
          displayPassword,
          passwordSet: Boolean(displayPassword) || u.passwordSet === true,
          companyName: companyLabel,
          enabled: u.enabled,
          vesselAccessMode: normalizeVesselAccessMode(u.vesselAccessMode),
          operatorSubRoleId: u.operatorSubRoleId ?? null,
          operatorSubRoleLabel: u.operatorSubRoleLabel ?? null,
        }
      })

      setUsers(mapped)
      setListAccess({ loading: false, accessDenied: false, loadError: null })
    } catch (e: any) {
      setUsers([])
      const next = resolveAdminListFetchError(e, 'Failed to load users')
      setListAccess(next)
      if (!next.accessDenied) {
        toast.error('Failed to load users', { position: 'top-center' })
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if ((actorCanPickCompanyGroup) && companyAdmins.length) {
      // re-map to include proper company names once we have them
      loadUsers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyAdmins])

  useEffect(() => {
    const refresh = () => { void loadUsers() }
    window.addEventListener('rbac-permissions-updated', refresh)
    return () => window.removeEventListener('rbac-permissions-updated', refresh)
  }, [])


  useEffect(() => {
    loadUsers()
    // If Superadmin OR Superadmin's Operator, also load company groups.
    if (actorCanPickCompanyGroup) {
      getCompanyAdminList()
        .then(setCompanyAdmins)
        .catch((e: any) => {
          toast.error('Failed to fetch companies', { position: 'top-center' })
          setCompanyAdmins([])
        })
    }
    // Load company-admin accounts for company owner creation.
    if (actorCanPickCompanyGroup || actorOwnCompanyGroupScope) {
      getCompanyList()
        .then(setCompanyAdminAccounts)
        .catch((e: any) => {
          toast.error('Failed to fetch company accounts', { position: 'top-center' })
          setCompanyAdminAccounts([])
        })
    } else {
      setCompanyAdminAccounts([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myCompanyGroupAdminId, actorCanPickCompanyGroup, actorOwnCompanyGroupScope])

  useEffect(() => {
    let cancelled = false
    setRoleFilterCatalogLoading(true)
    const cgaParam =
      isSuperAdmin || actorIsSuperAdmin
        ? undefined
        : actorCanPickCompanyGroup && selectedCompanyId != null
          ? Number(selectedCompanyId)
          : myCompanyGroupAdminId != null
            ? Number(myCompanyGroupAdminId)
            : undefined
    getRolesList(true, cgaParam)
      .then((list) => {
        if (!cancelled) setRoleFilterCatalog(Array.isArray(list) ? list : [])
      })
      .catch((e: any) => {
        if (!cancelled) {
          toast.error('Failed to load roles for filter', { position: 'top-center' })
          setRoleFilterCatalog([])
        }
      })
      .finally(() => {
        if (!cancelled) setRoleFilterCatalogLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [actorCanPickCompanyGroup, actorIsSuperAdmin, isSuperAdmin, selectedCompanyId, myCompanyGroupAdminId])

  useEffect(() => {
    if (roleFilterCatalogLoading || roleFilter === 'ALL') return
    const id = Number(roleFilter)
    if (!Number.isFinite(id)) {
      setRoleFilter('ALL')
      return
    }
    if (!sortedRoleFilterCatalog.some((r) => r.id === id)) {
      setRoleFilter('ALL')
    }
  }, [sortedRoleFilterCatalog, roleFilter, roleFilterCatalogLoading])



  useEffect(() => {
    if (!newUser.name) return
    if (!newUser.username) {
      const s = suggestUsername(newUser.name, takenUsernames)
      setNewUser(u => ({ ...u, username: s }))
    }
  }, [newUser.name]) // eslint-disable-line

  useEffect(() => {
    if (!ownerMode) {
      setSelectedOwnerCompanyAdminId(null)
      return
    }
    if (
      selectedOwnerCompanyAdminId != null &&
      !filteredOwnerCompanyAdmins.some((company) => Number(company.id) === Number(selectedOwnerCompanyAdminId))
    ) {
      setSelectedOwnerCompanyAdminId(null)
    }
  }, [ownerMode, filteredOwnerCompanyAdmins, selectedOwnerCompanyAdminId])

  useEffect(() => {
    if (!showAddUserForm) return
    let cancelled = false
    setAssignableRolesLoading(true)
    setAssignableRolesError('')
    const cgaParam =
      isSuperAdmin || actorIsSuperAdmin
        ? undefined
        : actorCanPickCompanyGroup && selectedCompanyId != null
          ? Number(selectedCompanyId)
          : myCompanyGroupAdminId != null
            ? Number(myCompanyGroupAdminId)
            : undefined
    getAssignableRolesForUserCreate(true, cgaParam)
      .then((list) => {
        if (!cancelled) setAssignableRolesForCreate(Array.isArray(list) ? list : [])
      })
      .catch((e: any) => {
        if (!cancelled) {
          setAssignableRolesError('Failed to load roles')
          setAssignableRolesForCreate([])
        }
      })
      .finally(() => {
        if (!cancelled) setAssignableRolesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [showAddUserForm, selectedCompanyId, myCompanyGroupAdminId, actorCanPickCompanyGroup, actorIsSuperAdmin, isSuperAdmin])

  useEffect(() => {
    if (assignableRolesLoading) return
    if (addUserRoleId === '') return
    if (!creatableAssignableRolesForAddUser.some((role) => role.id === addUserRoleId)) {
      setAddUserRoleId('')
    }
  }, [addUserRoleId, creatableAssignableRolesForAddUser, assignableRolesLoading])

  useEffect(() => {
    const open = searchParams.get('openAddUser')
    const rid = searchParams.get('prefillRoleId')
    if (open !== '1' || rid == null || rid === '') return
    const n = Number(rid)
    if (!Number.isFinite(n) || n <= 0) return
    setShowAddUserForm(true)
    setAddUserRoleId(n)
    const next = new URLSearchParams(searchParams)
    next.delete('openAddUser')
    next.delete('prefillRoleId')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // useEffect(() => {
  //   // Transform API nodes -> MenuStructure
  //   const buildFromApi = (nodes: MenuTreeNode[] | MenuTreeNode): MenuStructure => {
  //     const root: MenuTreeNode[] = Array.isArray(nodes) ? nodes : [nodes]
  //     const out: MenuStructure = {}

  //     root
  //       .filter((m) => m.level === 'MODULE')
  //       .forEach((mod) => {
  //         const moduleName = mod.name
  //         const menus: string[] = []
  //         const submenus: Record<string, string[]> = {}

  //         ;(mod.children || [])
  //           .filter((c) => c.level === 'MENU' && c.canView)
  //           .forEach((menuNode) => {
  //             menus.push(menuNode.name)

  //             const visibleSubs = (menuNode.children || []).filter((s) => s.canView)
  //             if (visibleSubs.length > 0) {
  //               submenus[menuNode.name] = visibleSubs.map((s) => s.name)
  //             }
  //           })

  //         if (menus.length > 0) {
  //           out[moduleName] = { menus, submenus }
  //         }
  //       })

  //     return out
  //   }

  //   // Fallback: use your static menu map (role 1) exactly like before
  //   const buildFromStatic = (): MenuStructure => {
  //     try {
  //       const allMenus: MenuGroup[] = getMenusByRole(1)
  //       const struct: MenuStructure = {}

  //       if (allMenus.length > 0) {
  //         allMenus[0].menu.forEach((menuItem: MenuItem) => {
  //           const moduleName = menuItem.name
  //           const menus: string[] = []
  //           const submenus: Record<string, string[]> = {}

  //           if (menuItem.submenu && menuItem.submenu.length > 0) {
  //             menuItem.submenu.forEach((submenuItem: MenuItem) => {
  //               menus.push(submenuItem.name)
  //               if (submenuItem.submenu && submenuItem.submenu.length > 0) {
  //                 submenus[submenuItem.name] = submenuItem.submenu.map((nestedSub: MenuItem) => nestedSub.name)
  //               }
  //             })
  //           }

  //           struct[moduleName] = { menus, submenus }
  //         })
  //       }

  //       return struct
  //     } catch {
  //       return {}
  //     }
  //   }

  //   const load = async () => {
  //     setMenuLoading(true)
  //     setMenuError('')

  //     try {
  //       // If you have an auth token available, pass it: getMenusTree({ token })
  //       const data = await getMenusTree()
  //       const struct = buildFromApi(data)
  //       setMenuStructure(Object.keys(struct).length ? struct : buildFromStatic())
  //     } catch (err: any) {
  //       setMenuError(err?.message || 'Failed to load menus')
  //       setMenuStructure(buildFromStatic())
  //     } finally {
  //       setMenuLoading(false)
  //     }
  //   }

  //   load()
  // }, [])

  const resetEditVesselAccessState = () => {
    editVesselAccessRequestSeq.current += 1
    editAssignableVesselsRequestSeq.current += 1
    setEditVesselAccessLoading(false)
    setEditVesselAccessSaving(false)
    setEditVesselAccessError('')
    setEditVesselAccessMode('NONE')
    setEditAssignedVesselIds(new Set())
    setEditAssignableVessels([])
    setEditAssignableVesselsLoading(false)
    setEditAssignableVesselsError('')
  }

  const closeEditModal = () => {
    setEditUser(null)
    setEditForm({
      fullName: '',
      email: '',
      mobile: '',
      dateOfBirth: '',
      enabled: true,
      operatorSubRoleId: '',
    })
    setSavingEdit(false)
    resetEditVesselAccessState()
  }

  const openEditModal = (user: User) => {
    const isOperatorTarget = isVesselAccessManagedRole(user)
    resetEditVesselAccessState()
    setEditUser(user)
    setEditForm({
      fullName: user.name || '',
      email: user.email || '',
      mobile: user.mobile || '',
      dateOfBirth: user.dateOfBirth ? String(user.dateOfBirth).slice(0, 10) : '',
      enabled: user.enabled !== false,
      operatorSubRoleId:
        operatorSubRolesUiEnabled && isOperatorTarget && user.operatorSubRoleId != null
          ? String(user.operatorSubRoleId)
          : '',
    })
  }

  const handleSaveEditUser = async () => {
    if (!editUser) return
    const isOperatorTarget = isVesselAccessManagedRole(editUser)
    if (isOperatorTarget && editVesselAccessMode === 'ASSIGNED' && editAssignedVesselIds.size === 0) {
      toast.error('Please select at least one vessel for ASSIGNED access.', { position: 'top-center' })
      return
    }
    setSavingEdit(true)
    setEditVesselAccessSaving(isOperatorTarget)
    try {
      const payload: Record<string, unknown> = {
        fullName: editForm.fullName.trim() || undefined,
        email: editForm.email.trim() || undefined,
        mobile: editForm.mobile.trim() || undefined,
        dateOfBirth: editForm.dateOfBirth || undefined,
        enabled: editForm.enabled,
      }
      if (operatorSubRolesUiEnabled && isOperatorTarget) {
        payload.operatorSubRoleId =
          editForm.operatorSubRoleId !== '' ? Number(editForm.operatorSubRoleId) : undefined
      }
      const updatedUserRes = await updateManagementUser(editUser.userId, payload)
      if (isOperatorTarget) {
        await updateUserVesselAccess(editUser.userId, {
          accessMode: editVesselAccessMode,
          vesselIds:
            editVesselAccessMode === 'ASSIGNED'
              ? Array.from(editAssignedVesselIds)
              : [],
        })
      }
      setUsers((prevUsers) =>
        prevUsers.map((u) => {
          if (u.userId === editUser.userId) {
            return {
              ...u,
              enabled: editForm.enabled,
              name: editForm.fullName.trim() || u.name,
              email: editForm.email.trim() || u.email,
              mobile: editForm.mobile.trim() || u.mobile,
              dateOfBirth: editForm.dateOfBirth || u.dateOfBirth,
              ...(updatedUserRes && updatedUserRes.enabled != null
                ? { enabled: updatedUserRes.enabled }
                : {}),
            }
          }
          return u
        })
      )
      toast.success('User updated', { position: 'top-center' })
      closeEditModal()
      await loadUsers()
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update user', { position: 'top-center' })
    } finally {
      setSavingEdit(false)
      setEditVesselAccessSaving(false)
    }
  }

  useEffect(() => {
    if (!editUser) {
      resetEditVesselAccessState()
      return
    }
    if (!isVesselAccessManagedRole(editUser)) {
      resetEditVesselAccessState()
      return
    }

    const requestId = ++editVesselAccessRequestSeq.current
    let cancelled = false

    setEditVesselAccessLoading(true)
    setEditVesselAccessError('')
    setEditAssignableVessels([])
    setEditAssignableVesselsError('')
    setEditVesselAccessMode('NONE')
    setEditAssignedVesselIds(new Set())

    getUserVesselAccess(editUser.userId)
      .then((response) => {
        if (cancelled || requestId !== editVesselAccessRequestSeq.current) return
        const mode = response?.accessMode ?? 'NONE'
        setEditVesselAccessMode(mode)
        setEditAssignedVesselIds(new Set((response?.vessels || []).map((item) => Number(item.id))))
      })
      .catch((error: any) => {
        if (cancelled || requestId !== editVesselAccessRequestSeq.current) return
        setEditVesselAccessError(error?.message || 'Failed to load vessel access')
      })
      .finally(() => {
        if (cancelled || requestId !== editVesselAccessRequestSeq.current) return
        setEditVesselAccessLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [editUser?.userId, editUser?.roleBehaviorType, editUser?.roleName])

  useEffect(() => {
    if (!editUser || !isVesselAccessManagedRole(editUser) || editVesselAccessMode !== 'ASSIGNED') {
      return
    }

    const requestId = ++editAssignableVesselsRequestSeq.current
    let cancelled = false

    setEditAssignableVesselsLoading(true)
    setEditAssignableVesselsError('')

    getAssignableVesselsForUser(editUser.userId)
      .then((vessels) => {
        if (cancelled || requestId !== editAssignableVesselsRequestSeq.current) return
        setEditAssignableVessels(Array.isArray(vessels) ? vessels : [])
      })
      .catch((error: any) => {
        if (cancelled || requestId !== editAssignableVesselsRequestSeq.current) return
        setEditAssignableVessels([])
        setEditAssignableVesselsError(error?.message || 'Failed to load assignable vessels')
      })
      .finally(() => {
        if (cancelled || requestId !== editAssignableVesselsRequestSeq.current) return
        setEditAssignableVesselsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [editUser?.userId, editUser?.roleBehaviorType, editUser?.roleName, editVesselAccessMode])

  const closeResetPasswordModal = () => {
    setResetUser(null)
    setResetForm({ newPassword: '', confirmPassword: '' })
    setShowResetPwd(false)
    setResetSuccessPassword(null)
    setResetPasswordCopied(false)
  }

  const handleGenerateResetPassword = () => {
    if (!resetUser) return
    let generated = ''
    for (let attempt = 0; attempt < 12; attempt++) {
      const candidate = generateTemporaryPassword(12)
      const check = validatePassword(candidate, {
        name: resetUser.name,
        email: resetUser.email,
      })
      if (check.ok) {
        generated = candidate
        break
      }
    }
    if (!generated) {
      generated = generateTemporaryPassword(12)
    }
    setResetForm({ newPassword: generated, confirmPassword: generated })
    setShowResetPwd(true)
    setResetSuccessPassword(null)
    setResetPasswordCopied(false)
  }

  const copyResetPasswordToClipboard = (password: string) => {
    navigator.clipboard
      .writeText(password)
      .then(() => {
        setResetPasswordCopied(true)
        toast.success('Password copied to clipboard!', { position: 'top-center', autoClose: 2000 })
        setTimeout(() => setResetPasswordCopied(false), 2000)
      })
      .catch(() => {
        toast.error('Failed to copy password', { position: 'top-center' })
      })
  }

  const handleResetPasswordSubmit = async () => {
    if (!resetUser) return
    const pw = validatePassword(resetForm.newPassword, {
      name: resetUser.name,
      email: resetUser.email,
    })
    if (!pw.ok) {
      toast.error(`Weak password:\n- ${pw.errors.join('\n- ')}`, { position: 'top-center' })
      return
    }
    if (resetForm.newPassword !== resetForm.confirmPassword) {
      toast.error('Passwords do not match', { position: 'top-center' })
      return
    }
    const submittedPassword = resetForm.newPassword
    setResettingPassword(true)
    try {
      await resetUserPassword(resetUser.userId, {
        newPassword: submittedPassword,
        confirmPassword: resetForm.confirmPassword,
      })
      toast.success('Password reset successfully', { position: 'top-center' })
      setResetSuccessPassword(submittedPassword)
      setResetForm({ newPassword: '', confirmPassword: '' })
      setShowResetPwd(false)
      await loadUsers()
    } catch (e: any) {
      const msg =
        'Failed to reset password'
      toast.error(msg, { position: 'top-center' })
    } finally {
      setResettingPassword(false)
    }
  }

  const handleCreateUser = async () => {
    const { name, email, mobile, dateOfBirth, username, password, confirmPassword } = newUser

    // light checks
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Please enter a valid email address', { position: 'top-center' })
      return
    }
    if (!/^\d{7,15}$/.test(mobile)) {
      toast.error('Phone must be 7-15 digits', { position: 'top-center' })
      return
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
      toast.error('Please pick a valid date of birth', { position: 'top-center' })
      return
    }

    // required fields
    if (!name || !email || !mobile || !dateOfBirth) {
      toast.error('Please fill all required fields', { position: 'top-center' })
      return
    }

    // username rules
    if (!USERNAME_REGEX.test(username)) {
      toast.error('Username must start with a letter and use 3-32 letters/numbers/._-', { position: 'top-center' })
      return
    }

    // live availability
    if (usernameExists(username, users)) {
      toast.error('Username already exists. Please choose another.', { position: 'top-center' })
      return
    }

    // password rules
    const pw = validatePassword(password, { name, email })
    if (!pw.ok) {
      toast.error(`Weak password:\n- ${pw.errors.join('\n- ')}`, { position: 'top-center' })
      return
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match', { position: 'top-center' })
      return
    }

    if (addUserRoleId === '' || !Number.isFinite(Number(addUserRoleId))) {
      toast.error('Please select a role.', { position: 'top-center' })
      return
    }
    const selectedRoleMeta = assignableRolesForCreate.find((r) => r.id === Number(addUserRoleId))
    const eff = effectiveBehaviorOfAssignableRole(selectedRoleMeta)
    const CREATION_SUPPORTED = new Set(['SHORE', 'COMPANY_OWNER', 'OWNER_APPROVER', 'OPERATOR', 'SUPERINTENDENT'])
    if (
      !selectedRoleMeta ||
      selectedRoleMeta.assignable === false ||
      (!selectedRoleMeta.customRole && (!eff || !CREATION_SUPPORTED.has(eff)))
    ) {
      toast.error(
        eff
          ? `User Management create only supports Operator, Superintendent, Company Owner, and eligible custom roles. (${eff.replace(/_/g, ' ')})`
          : 'This role cannot be created from this form.',
        { position: 'top-center' },
      )
      return
    }

    // role-based IDs / company binding
    let companyAdminId: number | null = null // always null for operator creation path
    let companyGroupAdminId: number | null = null

    if (eff === 'SHORE' || eff === 'COMPANY_OWNER' || eff === 'OWNER_APPROVER') {
      if (!actionGates.canCreateUser) {
        toast.error('You do not have permission to create users.', { position: 'top-center' })
        return
      }
      if (!actorCanPickCompanyGroup && !actorOwnCompanyGroupScope) {
        toast.error('This role cannot create company owners.', { position: 'top-center' })
        return
      }

      const selectedCompanyAdmin = selectedOwnerCompanyAdminId
        ? filteredOwnerCompanyAdmins.find((company) => Number(company.id) === Number(selectedOwnerCompanyAdminId))
        : null
      let selectedCompanyGroupAdminId: number | null = null
      if (actorCanPickCompanyGroup) {
        if (selectedCompanyId == null) {
          toast.error('Please select a company group for this owner.', { position: 'top-center' })
          return
        }
        selectedCompanyGroupAdminId = Number(selectedCompanyId)
      } else {
        selectedCompanyGroupAdminId = myCompanyGroupAdminId != null ? Number(myCompanyGroupAdminId) : null
        if (selectedCompanyGroupAdminId == null) {
          toast.error('Missing company group context for this owner.', { position: 'top-center' })
          return
        }
      }
      if (selectedOwnerCompanyAdminId && !selectedCompanyAdmin) {
        toast.error('Selected company account is out of your scope.', { position: 'top-center' })
        return
      }

      try {
        setCreating(true)
        const createdCompanyOwner = await createCompanyOwner({
          username,
          password,
          name,
          roleId: Number(addUserRoleId),
          companyGroupAdminId: selectedCompanyGroupAdminId,
          companyAdminId: selectedOwnerCompanyAdminId ? Number(selectedOwnerCompanyAdminId) : undefined,
        })
        setPendingOperatorAccessUserId(
          isVesselAccessManagedRole(selectedRoleMeta) && createdCompanyOwner?.userId != null
            ? Number(createdCompanyOwner.userId)
            : null
        )

        await loadUsers()

        const BASE = app_link + '/auth'
        const companySlug = resolveCompanySlug(selectedCompanyGroupAdminId, companyAdmins, currentUser)
        const loginUrl = companySlug ? `${BASE}/${companySlug}` : BASE

        setNewCredentials({ username, password, loginUrl })
        setShowAddUserForm(false)
        setShowCredentialsModal(true)

        setNewUser({
          name: '',
          dateOfBirth: '',
          email: '',
          mobile: '',
          username: '',
          password: '',
          confirmPassword: '',
        })
        setSelectedOwnerCompanyAdminId(null)
        setNewCrewRankId('')
        setNewOperatorSubRoleId('')
        setAddUserRoleId('')

        toast.success('Company Owner created!', { position: 'top-center' })
      } catch (e: any) {
        toast.error('Failed to create company owner', { position: 'top-center' })
      } finally {
        setCreating(false)
      }
      return
    }

    if (!actionGates.canCreateUser) {
      toast.error('You do not have permission to create users.', { position: 'top-center' })
      return
    }

    const operatorScope = resolveOperatorCreateScope({
      currentUser,
      actorOpts,
      assignTarget,
      selectedCompanyId,
    })
    if (!operatorScope.ok) {
      const reason = (operatorScope as { reason?: string }).reason
      if (reason === 'pick-company') {
        toast.error('Please select a company for this user.', { position: 'top-center' })
      } else if (reason === 'missing-cga') {
        toast.error('Missing company group context for this user.', { position: 'top-center' })
      } else {
        toast.error('You cannot create users with the selected role in your scope.', { position: 'top-center' })
      }
      return
    }
    companyGroupAdminId = operatorScope.companyGroupAdminId
    companyAdminId = operatorScope.companyAdminId

    if (eff === 'CREW' && (newCrewRankId === '' || !Number.isFinite(Number(newCrewRankId)))) {
      toast.error('Please select a crew rank.', { position: 'top-center' })
      return
    }

    try {
      setCreating(true)
      const createdOperator = await createOperator({
        username,
        password,
        name,
        email,
        contact_no: mobile,
        date_of_birth: `${dateOfBirth}T00:00:00`,
        companyAdminId: companyAdminId ?? null,
        companyGroupAdminId: companyGroupAdminId ?? null,
        roleId: Number(addUserRoleId),
        operatorSubRoleId:
          operatorSubRolesUiEnabled &&
            eff === 'OPERATOR' &&
            newOperatorSubRoleId !== '' &&
            Number.isFinite(Number(newOperatorSubRoleId))
            ? Number(newOperatorSubRoleId)
            : null,
        rankId: eff === 'CREW' ? Number(newCrewRankId) : undefined,
      })

      setPendingOperatorAccessUserId(
        isVesselAccessManagedRole(selectedRoleMeta) && createdOperator.userId != null
          ? Number(createdOperator.userId)
          : null
      )

      await loadUsers()

      const BASE = app_link+'/auth'
      let loginUrl = BASE

      if (companyGroupAdminId != null) {
        const slug = resolveCompanySlug(companyGroupAdminId, companyAdmins, currentUser)
        if (slug) loginUrl = `${BASE}/${slug}`
      }

      setNewCredentials({ username, password, loginUrl })
      setShowAddUserForm(false)
      setShowCredentialsModal(true)

      setNewUser({
        name: '',
        dateOfBirth: '',
        email: '',
        mobile: '',
        username: '',
        password: '',
        confirmPassword: '',
      })
      setNewOperatorSubRoleId('')
      setNewCrewRankId('')
      setAddUserRoleId('')

      toast.success('User created!', { position: 'top-center' })
    } catch (e: any) {
      toast.error('Failed to create user', { position: 'top-center' })
    } finally {
      setCreating(false)
    }
  }

  const saveUserEdits = async (row: User, newName: string) => {
    try {
      const opId = row.operatorId
      if (opId == null) {
        toast.error('Cannot update: missing operator record for this user.', { position: 'top-center' })
        return
      }
      const dob =
        row.dateOfBirth
          ? row.dateOfBirth.includes('T')
            ? row.dateOfBirth
            : `${row.dateOfBirth}T00:00:00`
          : null
      await updateOperator(opId, {
        username: row.username,
        name: newName,
        email: row.email || null,
        contact_no: row.mobile || null,
        date_of_birth: dob,
        companyAdminId: null,
        companyGroupAdminId: null,
        operatorSubRoleId: row.operatorSubRoleId ?? undefined,
        active: row.enabled !== false,
      })
      setUsers(prev => prev.map(u => (u.id === row.id ? { ...u, name: newName } : u)))
      toast.success('User updated', { position: 'top-center' })
    } catch (e: any) {
      toast.error('Failed to update user', { position: 'top-center' })
    }
  }

  const searchInPermissions = (searchValue: string) => {
    if (!searchValue.trim()) {
      setFilteredResults([]);
      return;
    }

    const results: SearchResult[] = [];
    const searchLower = searchValue.toLowerCase();

    Object.keys(menuStructure).forEach(moduleName => {
      const moduleData = menuStructure[moduleName];


      if (moduleName.toLowerCase().includes(searchLower)) {
        results.push({
          type: 'module',
          moduleName,
          menuName: null,
          submenuName: null,
          key: moduleName
        });
      }

      moduleData.menus.forEach((menuName: string) => {

        if (menuName.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'menu',
            moduleName,
            menuName,
            submenuName: null,
            key: `${moduleName}-${menuName}`
          });
        }


        const submenus = moduleData.submenus[menuName] || [];
        submenus.forEach((submenuName: string) => {
          if (submenuName.toLowerCase().includes(searchLower)) {
            results.push({
              type: 'submenu',
              moduleName,
              menuName,
              submenuName,
              key: `${moduleName}-${menuName}-${submenuName}`
            });
          }
        });
      });
    });

    setFilteredResults(results);
  };

  const scrollToResult = (key: string) => {
    const element = document.getElementById(`row-${key}`);
    if (element && tableRef.current) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      element.style.backgroundColor = '#fff3cd';
      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 2000);
    }
  };

  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const shouldShowRow = (moduleName: string, menuName: string, submenuName?: string) => {
    if (!permissionSearchTerm.trim()) return true;

    const searchLower = permissionSearchTerm.toLowerCase();
    return (
      moduleName.toLowerCase().includes(searchLower) ||
      menuName.toLowerCase().includes(searchLower) ||
      (submenuName && submenuName.toLowerCase().includes(searchLower))
    );
  };


  useEffect(() => {
    searchInPermissions(permissionSearchTerm);
  }, [permissionSearchTerm, menuStructure]);

  // Build the UI lists (module->menus->submenus) from API nodes (show ALL, ignore canView filter)
  const buildMenuStructureFromOperatorTree = (nodes: MenuTreeNode[]): MenuStructure => {
    const out: MenuStructure = {}
    nodes
      .filter(n => n.level === 'MODULE')
      .forEach(mod => {
        const moduleName = mod.name
        const menus: string[] = []
        const submenus: Record<string, string[]> = {}

          ; (mod.children || [])
            .filter(c => c.level === 'MENU')
            .forEach(menuNode => {
              if (isPermanentlyHiddenPlacement(moduleName, menuNode.name, null)) {
                return
              }
              menus.push(menuNode.name)

              const subs = (menuNode.children || []).filter(
                s => s.level === 'SUBMENU' && !isPermanentlyHiddenPlacement(moduleName, menuNode.name, s.name)
              )
              if (subs.length > 0) {
                submenus[menuNode.name] = subs.map(s => s.name)
              }
            })

        if (menus.length > 0) {
          out[moduleName] = { menus, submenus }
        }
      })
    return out
  }

  // returns true if any permission is true
  const anyPermTrue = (n: Pick<MenuTreeNode, 'canView' | 'canAdd' | 'canEdit' | 'canDelete' | 'canDownload'>) =>
    !!(n.canView || n.canAdd || n.canEdit || n.canDelete || n.canDownload)

  // Preselect menus/submenus and permission checkboxes from operator tree
  // Also build a key->ids map for the API (now includes MODULE->moduleId as well)
  const buildSelectionsFromOperatorTree = (nodes: MenuTreeNode[]) => {
    const permissions: Permissions = {}
    const selectedMenus = new Set<string>()
    const selectedSubmenus = new Set<string>()
    const ids: Record<string, { moduleId: number; menuId: number | null; submenuId: number | null; subsubmenuId: number | null }> = {}

    nodes.filter(n => n.level === 'MODULE').forEach(mod => {
      const moduleKey = `${mod.name}`

      // Map MODULE key -> ids
      ids[moduleKey] = {
        moduleId: mod.id,
        menuId: null,
        submenuId: null,
        subsubmenuId: null,
      }

        ; (mod.children || []).filter(m => m.level === 'MENU').forEach(menuNode => {
          if (isPermanentlyHiddenPlacement(mod.name, menuNode.name, null)) {
            return
          }
          const menuKey = `${mod.name}-${menuNode.name}`
          const menuSelfAny = anyPermTrue(menuNode)

          // Map MENU key -> ids
          ids[menuKey] = {
            moduleId: mod.id,
            menuId: menuNode.id,
            submenuId: null,
            subsubmenuId: null,
          }

          // Submenus
          const subs = (menuNode.children || []).filter(
            s => s.level === 'SUBMENU' && !isPermanentlyHiddenPlacement(mod.name, menuNode.name, s.name)
          )
          let anyChild = false

          subs.forEach(sub => {
            const subKey = `${mod.name}-${menuNode.name}-${sub.name}`
            const subAny = anyPermTrue(sub)
            if (subAny) selectedSubmenus.add(subKey)

            // Map SUBMENU key -> ids
            ids[subKey] = {
              moduleId: mod.id,
              menuId: menuNode.id,
              submenuId: sub.id,
              subsubmenuId: null, // not provided by tree yet
            }

            permissions[subKey] = {
              view: !!sub.canView,
              add: !!sub.canAdd,
              edit: !!sub.canEdit,
              delete: !!sub.canDelete,
              // Existing ACL model has no separate approve flag; mirror edit as approve.
              approve: !!sub.canEdit,
              download: !!sub.canDownload,
            }
            anyChild = anyChild || subAny
          })

          if (subs.length === 0) {
            if (menuSelfAny) selectedMenus.add(menuKey)
            permissions[menuKey] = {
              view: !!menuNode.canView,
              add: !!menuNode.canAdd,
              edit: !!menuNode.canEdit,
              delete: !!menuNode.canDelete,
              // Existing ACL model has no separate approve flag; mirror edit as approve.
              approve: !!menuNode.canEdit,
              download: !!menuNode.canDownload,
            }
          } else {
            if (anyChild || menuSelfAny) selectedMenus.add(menuKey)
          }
        })
    })

    return { permissions, selectedMenus, selectedSubmenus, ids }
  }




  // Permissions Modal Functions
  const openPermissionsModal = async (user: User) => {
    if (isSelfRow(user)) {
      toast.info('You cannot modify your own permissions.', { position: 'top-center' })
      return
    }

    setSelectedUser(user)
    setSelectedModule('')
    setSelectedMenu('')
    setSelectedSubmenu('')
    setPermissions({})
    setSelectedMenus(new Set())
    setSelectedSubmenus(new Set())
    setPermissionSearchTerm('')
    setFilteredResults([])
    setTreeBaselinePermissions({})
    setProcurementBaselineByCode({})
    setShowPermissionsModal(true)

    // Must have a userId to call /menus/tree/{userId}
    if (!user.userId) {
      toast.error('This record has no linked userId. Cannot load permissions.', { position: 'top-center' })
      setMenuStructure({})
      return
    }

    try {
      setMenuLoading(true)
      setMenuError('')

      // 1) Load user-specific tree
      const tree = await getUserMenusTree(user.userId)

      // 2) Build the UI structure (show ALL nodes)
      const struct = buildMenuStructureFromOperatorTree(tree)
      setMenuStructure(struct)

      // 3) Preselect + capture ids + baseline
      const {
        permissions: treePermissions,
        selectedMenus,
        selectedSubmenus,
        ids,
      } = buildSelectionsFromOperatorTree(tree)

      let semanticAllowedByCode: Record<string, boolean> = {}
      try {
        const procurementEffective = await getUserProcurementPermissions(user.userId)
        semanticAllowedByCode = Object.fromEntries(
          Object.entries(procurementEffective.permissions || {}).map(([code, value]) => [code, !!value?.allowed])
        )
      } catch (semanticError: any) {
        toast.warn(semanticError?.message || 'Procurement semantic permissions could not be loaded', {
          position: 'top-center',
        })
      }

      const mergedPermissions = overlayProcurementSemanticPermissions(
        treePermissions,
        Object.fromEntries(
          Object.entries(semanticAllowedByCode).map(([code, allowed]) => [code, { allowed }])
        )
      )

      setPermissions(mergedPermissions)
      setBaselinePermissions(mergedPermissions) // keep UI diff baseline
      setTreeBaselinePermissions(treePermissions)
      setProcurementBaselineByCode(semanticAllowedByCode)
      setSelectedMenus(selectedMenus)
      setSelectedSubmenus(selectedSubmenus)
      setKeyIdMap(ids)

    } catch (err: any) {
      setMenuError('Failed to load permissions')
      toast.error('Failed to load permissions', { position: 'top-center' })
      setMenuStructure({})
    } finally {
      setMenuLoading(false)
    }
  }


  const closePermissionsModal = () => {
    setShowPermissionsModal(false);
    setSelectedUser(null);
    setSelectedModule('');
    setSelectedMenu('');
    setSelectedSubmenu('');
    setPermissions({});
    setSelectedMenus(new Set());
    setSelectedSubmenus(new Set());
    setPermissionSearchTerm('');
    setFilteredResults([]);
    setTreeBaselinePermissions({});
    setProcurementBaselineByCode({});
  }

  const handleModuleChange = (module: string) => {
    setSelectedModule(module);
    setSelectedMenu('');
    setSelectedSubmenu('');
  }

  const handleMenuChange = (menu: string) => {
    setSelectedMenu(menu);
    setSelectedSubmenu('');
  }

  const handleSubmenuChange = (submenu: string) => {
    setSelectedSubmenu(submenu);
  }

  const handlePermissionChange = (itemKey: string, permissionType: keyof PermissionSet, checked: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [itemKey]: {
        view: prev[itemKey]?.view || false,
        add: prev[itemKey]?.add || false,
        edit: prev[itemKey]?.edit || false,
        delete: prev[itemKey]?.delete || false,
        approve: prev[itemKey]?.approve || false,
        download: prev[itemKey]?.download || false,
        [permissionType]: checked
      }
    }));
  }

  const handleMenuSelection = (moduleName: string, menuName: string, checked: boolean) => {

    if (!moduleName && menuName) {

      const moduleKey = menuName;
      const newSelectedMenus = new Set(selectedMenus);

      if (checked) {
        newSelectedMenus.add(moduleKey);
      } else {
        newSelectedMenus.delete(moduleKey);
      }

      setSelectedMenus(newSelectedMenus);
      return;
    }


    const menuKey = `${moduleName}-${menuName}`;
    const newSelectedMenus = new Set(selectedMenus);

    if (checked) {
      newSelectedMenus.add(menuKey);
    } else {
      newSelectedMenus.delete(menuKey);

      const moduleData = menuStructure[moduleName];
      if (moduleData && moduleData.submenus[menuName]) {
        const submenus = moduleData.submenus[menuName];
        const newSelectedSubmenus = new Set(selectedSubmenus);
        submenus.forEach((submenu: string) => {
          const submenuKey = `${moduleName}-${menuName}-${submenu}`;
          newSelectedSubmenus.delete(submenuKey);
        });
        setSelectedSubmenus(newSelectedSubmenus);
      }
    }

    setSelectedMenus(newSelectedMenus);
  }

  const handleSubmenuSelection = (moduleName: string, menuName: string, submenuName: string, checked: boolean) => {
    const submenuKey = `${moduleName}-${menuName}-${submenuName}`;
    const menuKey = `${moduleName}-${menuName}`;
    const newSelectedSubmenus = new Set(selectedSubmenus);
    const newSelectedMenus = new Set(selectedMenus);

    if (checked) {
      newSelectedSubmenus.add(submenuKey);

      newSelectedMenus.add(menuKey);
    } else {
      newSelectedSubmenus.delete(submenuKey);
    }

    setSelectedSubmenus(newSelectedSubmenus);
    setSelectedMenus(newSelectedMenus);
  }

  const validatePermissions = () => {
    if (selectedMenus.size === 0) {
      toast.error('Please select at least one menu', {
        position: 'top-center'
      });
      return false;
    }
    for (const menuKey of Array.from(selectedMenus)) {
      const [moduleName, menuName] = menuKey.split('-');
      const moduleData = menuStructure[moduleName];
      const hasSubmenus = moduleData && moduleData.submenus[menuName] && moduleData.submenus[menuName].length > 0;

      if (hasSubmenus) {

        const hasSelectedSubmenu = moduleData.submenus[menuName].some(submenuName => {
          const submenuKey = `${moduleName}-${menuName}-${submenuName}`;
          return selectedSubmenus.has(submenuKey);
        });

        if (!hasSelectedSubmenu) {
          toast.error(`Please select at least one submenu for ${menuName}`, {
            position: 'top-center'
          });
          return false;
        }
      } else {
        if (isProcurementPermissionKey(menuKey)) {
          continue;
        }
        const menuPermissions = permissions[menuKey];
        if (!menuPermissions || (!menuPermissions.view && !menuPermissions.add && !menuPermissions.edit && !menuPermissions.delete && !menuPermissions.approve && !menuPermissions.download)) {
          toast.error(`Please select at least one permission for ${menuName}`, {
            position: 'top-center'
          });
          return false;
        }
      }
    }


    for (const submenuKey of Array.from(selectedSubmenus)) {
      if (isProcurementPermissionKey(submenuKey)) {
        continue;
      }
      const submenuPermissions = permissions[submenuKey];
      if (!submenuPermissions || (!submenuPermissions.view && !submenuPermissions.add && !submenuPermissions.edit && !submenuPermissions.delete && !submenuPermissions.approve && !submenuPermissions.download)) {
        const [moduleName, menuName, submenuName] = submenuKey.split('-');
        toast.error(`Please select at least one permission for ${submenuName}`, {
          position: 'top-center'
        });
        return false;
      }
    }

    return true;
  }

  const savePermissions = async () => {
    if (!validatePermissions()) return
    if (!selectedUser?.userId) {
      toast.error('Missing userId for ACL update', { position: 'top-center' })
      return
    }

    // 1) Build final permission map from the full tree key set (selected + unselected).
    // This ensures explicit unchecks are persisted as viewOverride=false.
    const defaultPermSet: PermissionSet = {
      view: false,
      add: false,
      edit: false,
      delete: false,
      approve: false,
      download: false,
    }
    const finalPermissions: Permissions = {}
    Object.keys(keyIdMap).forEach((key) => {
      finalPermissions[key] = permissions[key] || defaultPermSet
    })

    const selectedMenuKeys = Array.from(selectedMenus)
    const selectedSubmenuKeys = Array.from(selectedSubmenus)
    const moduleVisibilityCache = new Map<string, boolean>()
    const isModuleVisible = (moduleKey: string): boolean => {
      if (moduleVisibilityCache.has(moduleKey)) {
        return !!moduleVisibilityCache.get(moduleKey)
      }
      const prefix = `${moduleKey}-`
      const visible =
        selectedMenus.has(moduleKey) ||
        selectedMenuKeys.some((key) => key.startsWith(prefix)) ||
        selectedSubmenuKeys.some((key) => key.startsWith(prefix))
      moduleVisibilityCache.set(moduleKey, visible)
      return visible
    }

    const resolveVisibleInTree = (
      key: string,
      ids: { moduleId: number; menuId: number | null; submenuId: number | null; subsubmenuId: number | null }
    ): boolean => {
      const isModuleNode = ids.menuId == null && ids.submenuId == null && ids.subsubmenuId == null
      const isMenuNode = ids.menuId != null && ids.submenuId == null && ids.subsubmenuId == null
      const isSubmenuNode = ids.submenuId != null && ids.subsubmenuId == null
      const isSubsubmenuNode = ids.subsubmenuId != null

      if (isSubmenuNode) return selectedSubmenus.has(key)
      if (isSubsubmenuNode) {
        return selectedSubmenus.has(key) || selectedSubmenuKeys.some((subKey) => key.startsWith(`${subKey}-`))
      }
      if (isMenuNode) return selectedMenus.has(key)
      if (isModuleNode) return isModuleVisible(key)
      return false
    }

    // 2) Translate to API ACLs using our keyIdMap
    const acls = Object.entries(finalPermissions)
      .map(([key, p]) => {
        const ids = keyIdMap[key]
        if (!ids) return null
        const isProcurementKey = isProcurementPermissionKey(key)
        const baseTree = treeBaselinePermissions[key] || {
          view: false,
          add: false,
          edit: false,
          delete: false,
          approve: false,
          download: false,
        }
        const visibleInTree = resolveVisibleInTree(key, ids)
        const hasPermissionRow = !!permissions[key]

        const viewOverride = isProcurementKey
          ? visibleInTree
          : (visibleInTree ? (hasPermissionRow ? !!p.view : true) : false)
        const addOverride = isProcurementKey
          ? !!baseTree.add
          : (hasPermissionRow ? !!p.add : !!baseTree.add)
        const editOverride = isProcurementKey
          ? !!baseTree.edit
          : (hasPermissionRow ? (!!p.edit || !!p.approve) : !!baseTree.edit)
        const deleteOverride = isProcurementKey
          ? !!baseTree.delete
          : (hasPermissionRow ? !!p.delete : !!baseTree.delete)
        const downloadOverride = isProcurementKey
          ? !!baseTree.download
          : (hasPermissionRow ? !!p.download : !!baseTree.download)

        return {
          moduleId: ids.moduleId,
          menuId: ids.menuId,
          submenuId: ids.submenuId,
          subsubmenuId: ids.subsubmenuId,
          viewOverride,
          addOverride,
          editOverride,
          deleteOverride,
          downloadOverride,
        }
      })
      .filter(Boolean) as {
        moduleId: number
        menuId: number | null
        submenuId: number | null
        subsubmenuId: number | null
        viewOverride: boolean
        addOverride: boolean
        editOverride: boolean
        deleteOverride: boolean
        downloadOverride: boolean
      }[]


    if (acls.length === 0) {
      toast.warn('No permissions to save', { position: 'top-center' })
      return
    }

    const desiredProcurementByCode = collectProcurementSemanticDecisions(permissions)
    const procurementOverrideUpdates = Object.entries(desiredProcurementByCode)
      .filter(([code, allowed]) => procurementBaselineByCode[code] !== allowed)
      .map(([permissionCode, allowed]) => ({
        permissionCode,
        allowed,
      }))

    try {
      setMenuLoading(true)

      // 3) PUT bulk ACL (replaceAll=true so this becomes the source of truth)
      await putBulkAcl([{
        userId: selectedUser.userId,
        replaceAll: true,
        acls
      }])

      if (procurementOverrideUpdates.length > 0) {
        await putUserProcurementPermissionOverrides(selectedUser.userId, procurementOverrideUpdates)
        window.dispatchEvent(new CustomEvent('rbac-permissions-updated', { detail: { userId: selectedUser.userId } }))
      }

      // 4) Refresh tree and rebuild UI state
      const [tree, refreshedProcurementEffective] = await Promise.all([
        getUserMenusTree(selectedUser.userId),
        getUserProcurementPermissions(selectedUser.userId),
      ])
      const struct = buildMenuStructureFromOperatorTree(tree)
      setMenuStructure(struct)

      const { permissions: refreshedTreePerms, selectedMenus: newSelMenus, selectedSubmenus: newSelSubs, ids: newIds } =
        buildSelectionsFromOperatorTree(tree)

      const refreshedSemanticAllowedByCode = Object.fromEntries(
        Object.entries(refreshedProcurementEffective.permissions || {}).map(([code, value]) => [code, !!value?.allowed])
      )
      const newPerms = overlayProcurementSemanticPermissions(
        refreshedTreePerms,
        Object.fromEntries(
          Object.entries(refreshedSemanticAllowedByCode).map(([code, allowed]) => [code, { allowed }])
        )
      )

      setPermissions(newPerms)
      setSelectedMenus(newSelMenus)
      setSelectedSubmenus(newSelSubs)
      setKeyIdMap(newIds)
      setTreeBaselinePermissions(refreshedTreePerms)
      setProcurementBaselineByCode(refreshedSemanticAllowedByCode)

      // 5) Diff (baseline vs new) to show what changed
      const changes: string[] = []
      const allKeys = new Set<string>([
        ...Object.keys(baselinePermissions),
        ...Object.keys(newPerms)
      ])

      const labelOf = (key: string) => key // you can prettify if desired

      allKeys.forEach(k => {
        const before = baselinePermissions[k] || { view: false, add: false, edit: false, delete: false, approve: false }
        const after = newPerms[k] || { view: false, add: false, edit: false, delete: false, approve: false }
          ; (['view', 'add', 'edit', 'delete'] as const).forEach(flag => {
            if (!!before[flag] !== !!after[flag]) {
              changes.push(`${labelOf(k)}: ${flag} ${before[flag] ? 'ON->OFF' : 'OFF->ON'}`)
            }
          })
      })

      setBaselinePermissions(newPerms) // new baseline post-save

      if (changes.length) {
        // keep toast concise
        const head = changes.slice(0, 6)
        toast.success(
          `Permissions updated. Changes:\n- ${head.join('\n- ')}${changes.length > 6 ? `\n- +${changes.length - 6} more...` : ''}`,
          { position: 'top-center' }
        )
      } else {
        toast.success('Permissions updated (no net changes).', { position: 'top-center' })
      }
    } catch (e: any) {
      toast.error('Failed to update permissions', { position: 'top-center' })
    } finally {
      setMenuLoading(false)
    }
  }


  const togglePasswordVisibility = (userId: number) => {
    const newVisible = new Set(visiblePasswords)
    if (newVisible.has(userId)) {
      newVisible.delete(userId)
    } else {
      newVisible.add(userId)
    }
    setVisiblePasswords(newVisible)
  }

  const copyToClipboard = () => {
    if (!newCredentials) return

    const loginDetails = `
URL: ${newCredentials.loginUrl}
Username: ${newCredentials.username}
Password: ${newCredentials.password}
`.trim()

    navigator.clipboard.writeText(loginDetails).then(() => {
      setCopied(true)
      toast.success('Login details copied to clipboard!', {
        position: 'top-center',
        autoClose: 2000
      })
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {
      toast.error('Failed to copy to clipboard', { position: 'top-center' })
    })
  }

  const copyPasswordToClipboard = (userId: number, password: string) => {
    navigator.clipboard.writeText(password).then(() => {
      setCopiedPasswords(prev => new Set([...Array.from(prev), userId]))
      toast.success('Password copied to clipboard!', {
        position: 'top-center',
        autoClose: 2000
      });
      setTimeout(() => {
        setCopiedPasswords(prev => {
          const newSet = new Set(prev)
          newSet.delete(userId)
          return newSet
        })
      }, 2000)
    }).catch(() => {
      toast.error('Failed to copy password', {
        position: 'top-center'
      });
    })
  }

  const closeCredentialsModal = () => {
    setShowCredentialsModal(false)
    setNewCredentials(null)
    setCopied(false)
    setPendingOperatorAccessUserId(null)
  }

  const openPendingOperatorVesselAccess = () => {
    if (pendingOperatorAccessUserId == null) {
      return
    }
    const target = users.find((row) => Number(row.userId) === Number(pendingOperatorAccessUserId))
    if (!target) {
      toast.error('Created operator not found in the list yet. Please refresh and try again.', {
        position: 'top-center',
      })
      return
    }
    closeCredentialsModal()
    openEditModal(target)
  }

  const handleSort = (key: keyof User) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }


  const targetVessel = useMemo(() => {
    if (!globalState.vessel) return null
    return (globalVessels || []).find((v: any) => v.id === globalState.vessel?.id) || globalState.vessel
  }, [globalState.vessel, globalVessels])

  const vesselCompanyNames = useMemo(() => {
    if (!targetVessel) return null
    const set = new Set<string>()
    const add = (s?: string | null) => {
      if (s && s.trim()) set.add(s.trim().toLowerCase())
    }
    add(targetVessel.companyGroupAdmin?.name)
    add((targetVessel as any).companyGroupAdmin?.companyGroupAdminName)
    add((targetVessel as any).companyGroupName)
    add(targetVessel.companyAdmin?.name)
    add((targetVessel as any).companyAdmin?.companyAdminName)
    add((targetVessel as any).companyName)

    const cgaId = targetVessel.companyGroupAdmin?.id ?? (targetVessel as any).companyGroupId ?? (targetVessel as any).companyGroupAdminId
    const caId = targetVessel.companyAdmin?.id ?? (targetVessel as any).companyId ?? (targetVessel as any).companyAdminId

    const matchedCga = (companyAdmins || []).find((c: any) => c.id === cgaId)
    const matchedCa = (companyAdminAccounts || []).find((c: any) => c.id === caId)
    if (matchedCga?.name) add(matchedCga.name)
    if (matchedCa?.name) add(matchedCa.name)

    return set
  }, [targetVessel, companyAdmins, companyAdminAccounts])

  const q = searchTerm.toLowerCase()
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      (user.name || '').toLowerCase().includes(q) ||
      (user.email || '').toLowerCase().includes(q) ||
      (user.username || '').toLowerCase().includes(q) ||
      ((actorCanPickCompanyGroup) && (user.companyName || '').toLowerCase().includes(q))

    const matchesRole =
      roleFilter === 'ALL' ||
      (user.roleId != null && String(user.roleId) === roleFilter)

    // Global filter: company
    let matchesCompany = true
    if (globalState.company) {
      matchesCompany = (user.companyName || '').toLowerCase() === globalState.company.name.toLowerCase()
    }

    // Global filter: vessel
    let matchesVessel = true
    if (vesselCompanyNames) {
      const uComp = (user.companyName || '').trim().toLowerCase()
      matchesVessel = Boolean(uComp && vesselCompanyNames.has(uComp))
    }

    return matchesSearch && matchesRole && matchesCompany && matchesVessel
  })



  const sortedUsers = React.useMemo(() => {
    let sortedRecords = [...filteredUsers]

    if (sortConfig.key !== null) {
      sortedRecords.sort((a, b) => {
        const aValRaw = (a as any)[sortConfig.key!]
        const bValRaw = (b as any)[sortConfig.key!]

        // numeric compare if both numbers
        if (typeof aValRaw === 'number' && typeof bValRaw === 'number') {
          return sortConfig.direction === 'asc' ? aValRaw - bValRaw : bValRaw - aValRaw
        }

        const aVal = String(aValRaw ?? '').toLowerCase()
        const bVal = String(bValRaw ?? '').toLowerCase()

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    } else {
      // Default: latest on top (assuming higher id = newer)
      sortedRecords.sort((a, b) => b.id - a.id)
    }

    return sortedRecords
  }, [filteredUsers, sortConfig])


  // Pagination
  const indexOfLastUser = currentPage * rowsPerPage
  const indexOfFirstUser = indexOfLastUser - rowsPerPage
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser)
  const totalPages = Math.ceil(sortedUsers.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const exportUsers = () => {
    // Use filtered + sorted list (ALL rows)
    const rows = sortedUsers

    const fmtDate = (s: string) => {
      if (!s) return '-'
      try {
        return new Date(s).toLocaleDateString()
      } catch {
        return s
      }
    }

    const now = new Date()
    const generatedOn = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`

    // Header cells
    const companyTh = showCompanyCol ? `<th>Company</th>` : ``

    const companyTd = (u: User) =>
      showCompanyCol
        ? `<td>${u.companyName ? u.companyName : '-'}</td>`
        : ``

    const subRoleExportLabel = (u: User) => {
      if (!isOperatorBehaviorValue(u.roleBehaviorType, u.roleName)) return '-'
      if (u.operatorSubRoleLabel && u.operatorSubRoleLabel.trim()) return u.operatorSubRoleLabel
      return '-'
    }

    // Body rows
    const rowsHtml = rows
      .map((u, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${u.name ?? ''}</td>
          ${companyTd(u)}
          <td>${u.dateOfBirth ? fmtDate(u.dateOfBirth) : '-'}</td>
          <td>${u.email ?? '-'}</td>
          <td>${u.mobile ?? '-'}</td>
          <td>${u.username ?? ''}</td>
          <td>${formatRoleLabel(u.roleName)}</td>
          <td>${subRoleExportLabel(u)}</td>
        </tr>
      `
      )
      .join('')

    const totalCols = 8 + (showCompanyCol ? 1 : 0)

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Users</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; margin: 24px; }
    h1 { margin: 0 0 4px; font-size: 18px; }
    .meta { font-size: 12px; color: #555; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
    th { background: #f5f5f5; text-align: left; }
    tfoot td { border: none; padding-top: 12px; font-size: 11px; color: #666; }
    @page { margin: 20mm; }
    @media print {
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      .no-print { display: none !important; }
      body { margin: 0; }
    }
    .page-footer:after {
      counter-increment: page;
      content: "Page " counter(page);
      float: right;
    }
  </style>
</head>
<body>
  <h1>User Management - Export</h1>
  <div class="meta">Generated on: ${generatedOn}</div>

  <table>
    <thead>
      <tr>
        <th style="width:70px;">SR/NO</th>
        <th>Name</th>
        ${companyTh}
        <th>Date of Birth</th>
        <th>Email</th>
        <th>Mobile</th>
        <th>Username</th>
        <th>Role</th>
        <th>Operator Sub-role</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="${totalCols}" class="page-footer">Total records: ${rows.length}</td>
      </tr>
    </tfoot>
  </table>

  <script>
    window.onload = function() {
      window.print();
      setTimeout(function(){ window.close(); }, 200);
    };
  </script>
</body>
</html>
  `.trim()

    const w = window.open('', '_blank')
    if (!w) return
    w.document.open()
    w.document.write(html)
    w.document.close()
  }



  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: "5rem" }}
      />
      <RolePermissionsModal
        open={rolePermissionsModalOpen}
        onClose={() => setRolePermissionsModalOpen(false)}
      />
      {permissionOverrideUser && (
        <UserPermissionOverridesModal
          open
          userId={permissionOverrideUser.userId}
          userLabel={`${permissionOverrideUser.name} (${permissionOverrideUser.username})`}
          onClose={() => setPermissionOverrideUser(null)}
          onSaved={() => {
            // Keep current User Management context (no route/tab switch after save).
          }}
        />
      )}

      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div className='d-flex align-items-center gap-3'>
                <h3 className='card-label text-dark fw-bold mb-0'>User Management</h3>
                {(loading || listAccess.loading) && (
                  <span className='badge badge-light-primary d-inline-flex align-items-center gap-2 py-2 px-3 fs-7 fw-normal'>
                    <span className='spinner-border spinner-border-sm text-primary' role='status' aria-hidden='true'></span>
                    Loading users...
                  </span>
                )}
              </div>
              <div className='card-toolbar d-flex align-items-center gap-3'>
                {canExportUsers && (
                  <button
                    type='button'
                    className='btn text-white d-inline-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#334155',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      gap: '6.5px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                      transition: 'all 0.2s ease',
                      border: 'none',
                      height: '36px'
                    }}
                    onClick={exportUsers}
                    title='Export Users'
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Export
                  </button>
                )}
                {actionGates.canOpenRolePermissions && (
                  <button
                    type='button'
                    className='btn text-white d-inline-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#7C3AED',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      gap: '6.5px',
                      boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)',
                      transition: 'all 0.2s ease',
                      border: 'none',
                      height: '36px'
                    }}
                    onClick={() => setRolePermissionsModalOpen(true)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    Roles &amp; Permissions
                  </button>
                )}
                {actionGates.canCreateUser && (
                  <button
                    type='button'
                    className='btn btn-primary text-white d-inline-flex align-items-center justify-content-center'
                    style={{
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      gap: '6.5px',
                      boxShadow: '0 2px 4px rgba(37,99,235,0.2)',
                      transition: 'all 0.2s ease',
                      border: 'none',
                      height: '36px'
                    }}
                    onClick={() => {
                      setAddUserRoleId('')
                      setNewOperatorSubRoleId('')
                      setNewCrewRankId('')
                      setShowAddUserForm(true)
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add User
                  </button>
                )}
              </div>
            </div>

            {/* Search and Filters */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='row g-3 align-items-end mb-4'>
                <div className='col-md-4'>
                  <label className='form-label text-muted fw-semibold fs-7 mb-2'>Search</label>
                  <div className='position-relative'>
                    <div
                      className='position-absolute ms-3'
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen021.svg'
                        className='svg-icon-2'
                      />
                    </div>
                    <input
                      type='text'
                      className='form-control form-control-sm ps-10'
                      placeholder='Search users...'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label text-muted fw-semibold fs-7 mb-2'>Role</label>
                  <SearchableSelect
                    className='form-select form-select-sm'
                    value={roleFilter}
                    disabled={roleFilterCatalogLoading && sortedRoleFilterCatalog.length === 0}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value='ALL'>All Roles</option>
                    {sortedRoleFilterCatalog.map((role) => (
                      <option key={role.id} value={String(role.id)}>
                        {formatRoleCatalogFilterLabel(role)}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
                <div className='col-md-4'>
                  <div className='d-flex justify-content-end'>
                    <button
                      type='button'
                      className='btn btn-outline-secondary btn-sm'
                      onClick={() => {
                        setSearchTerm('')
                        setRoleFilter('ALL')
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className='report-table table-responsive'>
                <table className='table table-bordered align-middle'>
                  <thead className='table-header text-start'>
                    <tr>
                      <th style={{ width: '90px' }}>SR/NO</th>
                      <th onClick={() => handleSort('name')} className='cursor-pointer' style={{ minWidth: '150px' }}>
                        <div className='d-flex align-items-center'>
                          NAME
                          <div style={{ transform: 'translateY(-2px)' }}>
                            <KTSVG
                              path={`/media/map/sort-col-${sortConfig.key === 'name'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                                }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </div>
                        </div>
                      </th>
                      {/* SUPERADMIN-ONLY COMPANY COLUMN */}
                      {(actorCanPickCompanyGroup) && (
                        <th onClick={() => handleSort('companyName' as keyof User)} className='cursor-pointer' style={{ minWidth: '180px' }}>
                          <div className='d-flex align-items-center'>
                            COMPANY
                            <div style={{ transform: 'translateY(-2px)' }}>
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'companyName'
                                  ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black'
                                  : 'grey'
                                  }.svg`}
                                className='svg-icon ms-2 custom-sort-icon'
                              />
                            </div>
                          </div>
                        </th>
                      )}
                      {/* <th onClick={() => handleSort('dateOfBirth')} className='cursor-pointer' style={{ minWidth: '120px' }}>
                        <div className='d-flex align-items-center'>
                          DATE OF BIRTH
                          <div style={{ transform: 'translateY(-2px)' }}>
                            <KTSVG
                              path={`/media/map/sort-col-${sortConfig.key === 'dateOfBirth'
                                ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black'
                                : 'grey'
                                }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </div>
                        </div>
                      </th> */}
                      <th onClick={() => handleSort('email')} className='cursor-pointer' style={{ minWidth: '200px' }}>
                        <div className='d-flex align-items-center'>
                          EMAIL
                          <div style={{ transform: 'translateY(-2px)' }}>
                            <KTSVG
                              path={`/media/map/sort-col-${sortConfig.key === 'email'
                                ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black'
                                : 'grey'
                                }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </div>
                        </div>
                      </th>
                      <th onClick={() => handleSort('mobile')} className='cursor-pointer' style={{ minWidth: '120px' }}>
                        <div className='d-flex align-items-center'>
                          MOBILE
                          <div style={{ transform: 'translateY(-2px)' }}>
                            <KTSVG
                              path={`/media/map/sort-col-${sortConfig.key === 'mobile'
                                ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black'
                                : 'grey'
                                }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </div>
                        </div>
                      </th>
                      <th onClick={() => handleSort('username')} className='cursor-pointer' style={{ minWidth: '120px' }}>
                        <div className='d-flex align-items-center'>
                          USERNAME
                          <div style={{ transform: 'translateY(-2px)' }}>
                            <KTSVG
                              path={`/media/map/sort-col-${sortConfig.key === 'username'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                                }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </div>
                        </div>
                      </th>
                      <th className='cursor-pointer' style={{ minWidth: '120px' }}>
                        <div className='d-flex align-items-center'>
                          ROLE
                          <div style={{ transform: 'translateY(-2px)' }}>
                            <KTSVG
                              path={`/media/map/sort-col-${sortConfig.key === 'roleName'
                                ? sortConfig.direction === 'asc'
                                  ? 'up-black'
                                  : 'down-black'
                                : 'grey'
                                }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </div>
                        </div>
                      </th>
                      {/* <th style={{ minWidth: '220px' }}>OPERATOR SUB-ROLE</th> */}
                      <th style={{ minWidth: '200px' }}>ACTIONS</th>
                    </tr>
                  </thead>

                  <tbody className='table-body text-start'>
                    {(loading || listAccess.loading) ? (
                      Array.from({ length: 8 }).map((_, i) => (
                        <tr key={`user-skeleton-${i}`}>
                          <td className='py-3' style={{ width: '90px' }}>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-6 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-8 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          {actorCanPickCompanyGroup && (
                            <td className='py-3'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-9 rounded-pill' style={{ height: 18 }}></span>
                              </div>
                            </td>
                          )}
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-10 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-7 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-6 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-8 rounded-pill' style={{ height: 18 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-5 rounded' style={{ height: 28 }}></span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : currentUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7 + (actorCanPickCompanyGroup ? 1 : 0)} className='text-center text-muted py-5'>
                          {adminListEmptyMessage(listAccess, 'No users found for the selected criteria.')}
                        </td>
                      </tr>
                    ) : (
                      currentUsers.map((user, idx) => (
                        <tr key={user.id}>
                          <td className='text-dark fs-6' style={{ width: '90px' }}>
                            {indexOfFirstUser + idx + 1}
                          </td>
                          <td className='text-dark fw-bold fs-6'>{user.name}</td>
                          {/* SUPERADMIN-ONLY COMPANY CELL */}
                          {(actorCanPickCompanyGroup) && (
                            <td className='text-dark fs-6'>
                              {user.companyName === 'Superadmin' ? (
                                <span
                                  className='badge rounded-pill bg-light text-muted border'
                                  style={{ opacity: 0.85, cursor: 'not-allowed' }}
                                  aria-disabled='true'
                                  title='This user is under Superadmin'
                                >
                                  Superadmin
                                </span>
                              ) : (
                                user.companyName || '-'
                              )}
                            </td>
                          )}

                          {/* <td className='text-dark fs-6'>
                            {user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : '-'}
                          </td> */}

                          <td className='text-dark fs-6'>{user.email || '-'}</td>

                          <td className='text-dark fs-6'>{user.mobile || '-'}</td>
                          <td className='text-dark fw-semibold fs-6' style={{ fontFamily: 'monospace' }}>
                            {user.username}
                          </td>
                          <td className='text-dark fs-6'>
                            {formatRoleLabel(user.roleName)}
                          </td>
                          {/* <td className='text-dark fs-6' style={{ minWidth: '200px' }}>
                            {(() => {
                              const isOp = isOperatorBehaviorValue(user.roleBehaviorType, user.roleName)
                              if (!isOp) {
                                return <span className='text-muted'>-</span>
                              }
                              return user.operatorSubRoleLabel && user.operatorSubRoleLabel.trim() ? (
                                <span>{user.operatorSubRoleLabel}</span>
                              ) : (
                                <span className='text-muted'>-</span>
                              )
                            })()}
                          </td> */}
                          <td className='text-dark fs-6'>
                            <div className='d-flex align-items-center'>
                              {/* <span style={{ fontFamily: 'monospace', minWidth: '80px' }}>
                                {user.displayPassword
                                  ? (visiblePasswords.has(user.id) ? user.displayPassword : '********')
                                  : '********'}
                              </span> */}
                              {actionGates.canUpdateUser && (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-primary ms-2'
                                  onClick={() => openEditModal(user)}
                                  title='Edit user'
                                >
                                  Edit
                                </button>
                                
                              )}
                              {/* {user.displayPassword ? (
                                <>
                                  <button
                                    onClick={() => togglePasswordVisibility(user.id)}
                                    className='btn btn-icon btn-sm ms-2'
                                    title={visiblePasswords.has(user.id) ? 'Hide password' : 'Show password'}
                                  >
                                    <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3' />
                                  </button>
                                  <button
                                    onClick={() => copyPasswordToClipboard(user.id, user.displayPassword || '')}
                                    className={`btn btn-icon btn-sm ms-1 ${copiedPasswords.has(user.id) ? 'btn-success' : ''}`}
                                    title={copiedPasswords.has(user.id) ? 'Copied!' : 'Copy password'}
                                  >
                                    <KTSVG path={`/media/icons/duotune/general/${copiedPasswords.has(user.id) ? 'gen043' : 'gen054'}.svg`} className='svg-icon-3' />
                                  </button>
                                </>
                              ) : (
                                <button
                                  className='btn btn-light btn-sm py-0 px-2 ms-2'
                                  disabled
                                  title='Password cannot be displayed for this user type. Use Reset Password.'
                                >
                                  Copy
                                </button>
                              )} */}
                                {(currentUser?.role.roleName == 'SUPER_ADMIN' || currentUser?.role.roleName == 'COMPANY_GROUP_ADMIN' )&& (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-warning ms-2'
                                  onClick={() => {
                                    setResetUser(user)
                                    setResetForm({ newPassword: '', confirmPassword: '' })
                                    setShowResetPwd(false)
                                    setResetSuccessPassword(null)
                                    setResetPasswordCopied(false)
                                  }}
                                  title='Reset Password'
                                >
                                  Reset Password
                                </button>
                              )}
                              {/* {currentUser?.role.roleName} */}
                              {currentUser?.role.roleName == 'SUPER_ADMIN' && (

                                // canShowRowResetPassword(user) && (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-info ms-2'
                                  onClick={async () => {
                                    if (!window.confirm(`Send password reset link to ${user.username || user.name}?`)) return
                                    try {
                                      await sendUserPasswordResetLink(user.userId)
                                      toast.success('Password reset link sent successfully', { position: 'top-center' })
                                    } catch (e: any) {
                                      const msg =
                                        'Failed to send reset link'
                                      toast.error(msg, { position: 'top-center' })
                                    }
                                  }}
                                  title='Send Reset Link via Email'
                                >
                                  Send Reset Link
                                </button>
                              )}
                              {(() => {
                                const isSelf = isSelfRow(user)
                                const canManageAccess =
                                  actionGates.canManageUserAccess &&
                                  user.userId != null &&
                                  Number.isFinite(Number(user.userId))
                                if (!actionGates.canManageUserAccess) return null
                                return (
                                  <>
                                    {/* <button
                                      type='button'
                                      onClick={() => {
                                        if (!canManageAccess || isSelf) return
                                        navigate(`/manage/role-permissions?userId=${user.userId}&tab=user-bundles`)
                                      }}
                                      className='btn btn-icon btn-sm ms-1 btn-light-primary'
                                      title={isSelf ? 'You cannot modify your own access' : 'Open package assignments (Role Permissions)'}
                                      disabled={!canManageAccess || isSelf}
                                      style={!canManageAccess || isSelf ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                    >
                                      <KTSVG path='/media/icons/duotune/technology/teh001.svg' className='svg-icon-3' />
                                    </button> */}
                                    <button
                                      type='button'
                                      onClick={() => {
                                        if (!canManageAccess || isSelf) return
                                        setPermissionOverrideUser(user)
                                      }}
                                      className='btn btn-icon btn-sm ms-1 btn-light-success'
                                      title={
                                        isSelf
                                          ? 'You cannot modify your own access'
                                          : 'Permission overrides (allow/deny vs role default)'
                                      }
                                      disabled={!canManageAccess || isSelf}
                                      style={!canManageAccess || isSelf ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                    >
                                      <KTSVG path='/media/icons/duotune/finance/fin006.svg' className='svg-icon-3' />
                                    </button>
                                  </>
                                )
                              })()}


                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className='pagination-wrapper d-flex justify-content-between align-items-center py-3'>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <SearchableSelect
                      className='form-select form-select-sm'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px'
                      }}
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </SearchableSelect>
                  </div>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                      Showing <strong>{((currentPage - 1) * rowsPerPage) + 1}-{Math.min(currentPage * rowsPerPage, sortedUsers.length)}</strong> of <strong>{sortedUsers.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px'
                            }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>

                        {(() => {
                          const pages = []
                          const showPages = 5
                          let startPage = Math.max(1, currentPage - 2)
                          let endPage = Math.min(totalPages, startPage + showPages - 1)

                          if (endPage - startPage < showPages - 1) {
                            startPage = Math.max(1, endPage - showPages + 1)
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handlePageChange(i)}
                                >
                                  {i}
                                </button>
                              </li>
                            )
                          }

                          return pages
                        })()}

                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px'
                            }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Form Modal */}
      {showAddUserForm && (
        <div
          className="modal fade show d-flex align-items-center justify-content-center"
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1050,
          }}
        >
          <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
            <div className='modal-content bg-white' style={{ color: '#181C32' }}>
              <form onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }}>
                <div className='modal-header'>
                  <h5 className='modal-title'>Add New User</h5>
                  <button
                    type='button'
                    className='btn-close'
                    onClick={() => setShowAddUserForm(false)}
                  />
                </div>

                <div className='modal-body'>
                  <div className='row g-3'>
                    <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Name</label>
                      <input
                        type='text'
                        className='form-control'
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        placeholder='Enter full name'
                        style={{ color: '#000' }}
                      />
                    </div>


                    <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                        Date of Birth
                      </label>
                      <input
                        type='date'
                        className='form-control'
                        value={newUser.dateOfBirth}
                        onChange={(e) => setNewUser({ ...newUser, dateOfBirth: e.target.value })}
                        style={{ color: '#000' }}
                      />
                    </div>

                    <div className='col-12'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                        Email
                      </label>
                      <input
                        type='email'
                        className='form-control'
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder='Enter email address'
                        style={{ color: '#000' }}
                      />
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                        Mobile
                      </label>
                      <input
                        type='tel'
                        className='form-control'
                        value={newUser.mobile}
                        onChange={(e) => setNewUser({ ...newUser, mobile: e.target.value })}
                        placeholder='Enter mobile number'
                        style={{ color: '#000' }}
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                        Role
                      </label>
                      <SearchableSelect
                        className='form-select'
                        value={addUserRoleId === '' ? '' : String(addUserRoleId)}
                        onChange={(e) => setAddUserRoleId(e.target.value ? Number(e.target.value) : '')}
                        disabled={assignableRolesLoading}
                        style={{ color: '#000' }}
                      >
                        <option value=''>Select role…</option>
                        {assignableRolesLoading ? (
                          <option value='' disabled>Loading roles...</option>
                        ) : creatableAssignableRolesForAddUser.length === 0 ? (
                          <option value='' disabled>No roles available</option>
                        ) : (
                          creatableAssignableRolesForAddUser.map((r) => (
                            <option key={r.id} value={r.id}>
                              {resolveAddUserRoleLabel(r)}
                            </option>
                          ))
                        )}
                      </SearchableSelect>
                      {selectedRoleForAdd ? (
                        <div className='mt-2 p-2 rounded border bg-light'>
                          <div className='d-flex flex-wrap align-items-center gap-2 mb-1'>
                            {(() => {
                              const m = assignableRoleTypeMeta(selectedRoleForAdd)
                              return <span className={m.badgeClass}>{m.label}</span>
                            })()}
                          </div>
                          <div className='small text-gray-800' style={{ lineHeight: 1.5 }}>
                            {selectedRoleSummaryLine}
                          </div>
                        </div>
                      ) : null}
                      {assignableRolesLoading && (
                        <small className='text-muted d-block mt-1'>Loading roles...</small>
                      )}
                      {assignableRolesError && !assignableRolesLoading && (
                        <small className='text-danger d-block mt-1'>{assignableRolesError}</small>
                      )}
                      {(actorCanPickCompanyGroup) && (
                        <small className='text-muted d-block mt-1'>
                          Selecting a company in &quot;Assign User Under&quot; or &quot;Company Group&quot; reloads roles for that company.
                        </small>
                      )}
                    </div>
                    {operatorSubRolesUiEnabled && operatorCreateMode && (
                      <div className='col-12'>
                        <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                          Operator sub-role (optional)
                        </label>
                        <SearchableSelect
                          className='form-select'
                          value={newOperatorSubRoleId === '' ? '' : String(newOperatorSubRoleId)}
                          onChange={(e) =>
                            setNewOperatorSubRoleId(e.target.value ? Number(e.target.value) : '')
                          }
                          disabled={operatorSubRolesLoading || operatorSubRoles.length === 0}
                          style={{ color: '#000' }}
                        >
                          <option value=''>Select operator sub-role (optional)</option>
                          {operatorSubRoles.map((sr) => (
                            <option key={sr.id} value={sr.id}>
                              {sr.name}
                            </option>
                          ))}
                        </SearchableSelect>
                        <small className='text-muted d-block mt-1'>
                          Choose the operator type. This controls the default access package.
                        </small>
                        {!operatorSubRolesLoading && operatorSubRoles.length === 0 && (
                          <small className='text-muted d-block mt-1'>
                            No operator sub-roles are available. You can still create a basic operator without one.
                          </small>
                        )}
                      </div>
                    )}
                    {vesselAccessCreateMode && (
                      <div className='col-12'>
                        <div className='alert alert-light border py-2 px-3 mb-0'>
                          {selectedRoleForAdd?.customRole
                            ? 'This custom-role user will start with no vessel access. Configure vessel access after the user is created.'
                            : addUserEffectiveBehavior === 'SUPERINTENDENT'
                              ? 'This Superintendent will start with no vessel access. Configure vessel access after the user is created.'
                              : 'This operator will start with no vessel access. Configure vessel access after the user is created.'}
                        </div>
                      </div>
                    )}
                    {crewCreateMode && (
                      <div className='col-12'>
                        <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                          Crew Rank
                        </label>
                        <SearchableSelect
                          className='form-select'
                          value={newCrewRankId === '' ? '' : String(newCrewRankId)}
                          onChange={(e) => setNewCrewRankId(e.target.value ? Number(e.target.value) : '')}
                          disabled={crewRanksLoading || crewRanks.length === 0}
                          style={{ color: '#000' }}
                        >
                          <option value=''>Select crew rank...</option>
                          {crewRanks.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.rank}
                            </option>
                          ))}
                        </SearchableSelect>
                        <small className='text-muted d-block mt-1'>
                          Choose the crew rank. This controls the default access package.
                        </small>
                        {!crewRanksLoading && crewRanks.length === 0 && (
                          <small className='text-danger d-block mt-1'>
                            No crew ranks are available. Check rank library setup and try refreshing the page.
                          </small>
                        )}
                      </div>
                    )}
                    {/* SUPERADMIN or SUPERADMIN'S OPERATOR: choose where to assign */}
                    {(vesselAccessCreateMode || crewCreateMode) && actorCanPickCompanyGroup && (
                      <>
                        <div className='col-12'>
                          <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                            Assign User Under
                          </label>
                          <div className='d-flex align-items-center gap-4'>
                            <div className='form-check'>
                              <input
                                className='form-check-input'
                                type='radio'
                                name='assignTarget'
                                id='assignSuperadmin'
                                checked={assignTarget === 'superadmin'}
                                onChange={() => setAssignTarget('superadmin')}
                              />
                              <label className='form-check-label' htmlFor='assignSuperadmin'>Superadmin</label>
                            </div>
                            <div className='form-check'>
                              <input
                                className='form-check-input'
                                type='radio'
                                name='assignTarget'
                                id='assignCompany'
                                checked={assignTarget === 'company'}
                                onChange={() => setAssignTarget('company')}
                              />
                              <label className='form-check-label' htmlFor='assignCompany'>A Company</label>
                            </div>
                          </div>
                        </div>

                        {assignTarget === 'company' && (
                          <div className='col-md-8'>
                            <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                              Select Company
                            </label>
                            <SearchableSelect
                              className='form-select'
                              value={selectedCompanyId ?? ''}
                              onChange={(e) => setSelectedCompanyId(e.target.value ? Number(e.target.value) : null)}
                            >
                              <option value='' disabled>Select company group admin...</option>
                              {companyAdmins.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.name || c.uid?.username}
                                </option>
                              ))}

                            </SearchableSelect>
                            {!companyAdmins.length && (
                              <small className='text-muted'>No companies found.</small>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {/* COMPANY OPERATOR: lock to their company (no dropdown / no superadmin options) */}
                    {(vesselAccessCreateMode || crewCreateMode) && actorIsScopedOperator && (
                      <div className='col-md-8'>
                        <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                          Assign User Under
                        </label>
                        <div className='d-flex align-items-center'>
                          <span
                            className='badge rounded-pill bg-light text-muted border'
                            title='This operator belongs to a company; new users will be created under the same company.'
                            style={{ opacity: 0.9 }}
                          >
                            Company (fixed)
                          </span>
                        </div>
                      </div>
                    )}
                    {ownerMode && (
                      <>
                        {(actorCanPickCompanyGroup) && (
                          <div className='col-md-8'>
                            <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                              Select Company Group
                            </label>
                            <SearchableSelect
                              className='form-select'
                              value={selectedCompanyId ?? ''}
                              onChange={(e) => setSelectedCompanyId(e.target.value ? Number(e.target.value) : null)}
                            >
                              <option value='' disabled>Select company group...</option>
                              {companyAdmins.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name || c.uid?.username}
                                </option>
                              ))}
                            </SearchableSelect>
                            {!companyAdmins.length && (
                              <small className='text-muted'>No company groups found.</small>
                            )}
                          </div>
                        )}
                        <div className='col-md-8'>
                          <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                            Company Account
                          </label>
                          <SearchableSelect
                            className='form-select'
                            value={selectedOwnerCompanyAdminId ?? ''}
                            onChange={(e) => setSelectedOwnerCompanyAdminId(e.target.value ? Number(e.target.value) : null)}
                          >
                            <option value=''>Optional - leave blank for company-group-level owner</option>
                            {filteredOwnerCompanyAdmins.map((company) => {
                              const companyUser = String((company as any)?.username || (company as any)?.uid?.username || '').trim()
                              const cgaName = String((company as any)?.cga?.name || '').trim()
                              return (
                                <option key={company.id} value={company.id}>
                                  {company.name || companyUser || `Company #${company.id}`}{cgaName ? ` (${cgaName})` : ''}
                                </option>
                              )
                            })}
                          </SearchableSelect>
                          {!filteredOwnerCompanyAdmins.length && (
                            <small className='text-muted'>No company accounts available in your scope.</small>
                          )}
                        </div>
                      </>
                    )}

                    <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                        Username
                      </label>
                      <div className='input-group'>
                        <input
                          type='text'
                          className='form-control'
                          value={newUser.username}
                          onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                          placeholder='e.g. john.doe'
                          style={{ color: '#000' }}
                        />
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => {
                            const s = suggestUsername(newUser.name || 'user', takenUsernames)
                            setNewUser(u => ({ ...u, username: s }))
                          }}
                          title='Suggest username'
                        >
                          Suggest
                        </button>
                      </div>
                      <small className='text-muted'>
                        Must start with a letter; 3-32 chars; letters, numbers, . _ -
                        {newUser.username && usernameExists(newUser.username, users) && (
                          <span className='ms-2 text-danger fw-semibold'>- already taken</span>
                        )}
                        {newUser.username && USERNAME_REGEX.test(newUser.username) && !usernameExists(newUser.username, users) && (
                          <span className='ms-2 text-success fw-semibold'>- available</span>
                        )}
                      </small>
                    </div>

                    {/* Password */}
                    <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                        Password
                      </label>
                      <div className='input-group'>
                        <input
                          type={showPwd ? 'text' : 'password'}
                          className='form-control'
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          placeholder='Create a strong password'
                          style={{ color: '#000' }}
                        />
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => setShowPwd(v => !v)}
                          title={showPwd ? 'Hide password' : 'Show password'}
                        >
                          {showPwd ? 'Hide' : 'Show'}
                        </button>
                      </div>

                      {(() => {
                        if (!newUser.password) return null
                        const res = validatePassword(newUser.password, { name: newUser.name, email: newUser.email })
                        return (
                          <small className={res.ok ? 'text-success' : 'text-danger'}>
                            {res.ok ? 'Strong password' : `Weak: ${res.errors.join(', ')}`}
                          </small>
                        )
                      })()}
                    </div>

                    {/* Confirm Password */}
                    <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                        Confirm Password
                      </label>
                      <div className='input-group'>
                        <input
                          type={showConfirmPwd ? 'text' : 'password'}
                          className='form-control'
                          value={newUser.confirmPassword}
                          onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                          placeholder='Re-enter password'
                          style={{ color: '#000' }}
                        />
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => setShowConfirmPwd(v => !v)}
                          title={showConfirmPwd ? 'Hide password' : 'Show password'}
                        >
                          {showConfirmPwd ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      {newUser.confirmPassword && newUser.password !== newUser.confirmPassword && (
                        <small className='text-danger'>Passwords do not match</small>
                      )}
                    </div>


                    <div className='col-12'>
                      <button
                        type='button'
                        className='btn btn-sm btn-outline-secondary mt-2'
                        onClick={() => {
                          // quick generator for convenience (meets rules)
                          const rand = () => Math.random().toString(36).slice(-4)
                          const generated = `Aa${rand()}${rand()}!${Math.floor(100 + Math.random() * 900)}`
                          setNewUser(u => ({ ...u, password: generated, confirmPassword: generated }))
                        }}
                      >
                        Generate Strong Password
                      </button>
                    </div>


                    {/* <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Department</label>
                      <SearchableSelect
                        className='form-control'
                        value={newUser.department}
                        onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                        style={{ color: '#000' }}
                      >
                        <option value="">Select Department</option>
                        {departments.map((dept) => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </SearchableSelect>
                    </div> */}
                  </div>
                </div>

                <div className='modal-footer'>
                  <button
                    type='button'
                    className='btn btn-light btn-sm'
                    onClick={() => setShowAddUserForm(false)}
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button type='submit' className='btn btn_primary' disabled={creating || assignableRolesLoading || !selectedAddUserRoleIsValid}>
                    {creating ? 'Creating...' : ownerMode ? 'Create Company Owner' : 'Create User'}
                  </button>

                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && newCredentials && (
        <div
          className="modal fade show d-flex align-items-center justify-content-center"
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1050,
          }}
        >
          <div className='modal-dialog modal-dialog-centered' role='document'>
            <div className='modal-content bg-white' style={{ color: '#181C32' }}>
              <div className='modal-header text-center'>
                <div className='w-100'>
                  <div className='d-flex justify-content-center mb-3'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{
                        width: '64px',
                        height: '64px',
                        backgroundColor: '#d4edda',
                        borderRadius: '50%'
                      }}
                    >
                      <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-2x text-success' />
                    </div>
                  </div>
                  <h5 className='modal-title'>User Created Successfully!</h5>
                </div>
                <button
                  type='button'
                  className='btn-close'
                  onClick={closeCredentialsModal}
                />
              </div>

              <div className='modal-body'>
                <div className='bg-light rounded p-4 mb-4'>
                  <h6 className='text-muted mb-3'>System Generated Credentials:</h6>
                  <div className='row'>
                    <div className='col-4'><span className='text-muted'>Login URL:</span></div>
                    <div className='col-8'>
                      <code style={{ wordBreak: 'break-all' }}>{newCredentials.loginUrl}</code>
                    </div>
                  </div>
                  <div className='row mt-2'>
                    <div className='col-4'>
                      <span className='text-muted'>Username:</span>
                    </div>
                    <div className='col-8'>
                      <span className='fw-bold' style={{ fontFamily: 'monospace' }}>{newCredentials.username}</span>
                    </div>
                  </div>
                  <div className='row mt-2'>
                    <div className='col-4'>
                      <span className='text-muted'>Password:</span>
                    </div>
                    <div className='col-8'>
                      <span className='fw-bold' style={{ fontFamily: 'monospace' }}>{newCredentials.password}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={copyToClipboard}
                  className={`btn w-100 mb-3 ${copied
                    ? 'btn-success'
                    : 'btn-light'
                    }`}
                >
                  <KTSVG
                    path={copied ? '/media/icons/duotune/general/gen043.svg' : '/media/icons/duotune/general/gen054.svg'}
                    className='svg-icon-2 me-2'
                  />
                  {copied ? 'Copied!' : 'Copy Login Details to Clipboard'}
                </button>

                {pendingOperatorAccessUserId != null && (
                  <button
                    type='button'
                    onClick={openPendingOperatorVesselAccess}
                    className='btn btn-light-primary w-100 mb-3'
                  >
                    Configure Vessel Access
                  </button>
                )}

                <button
                  onClick={closeCredentialsModal}
                  className='btn btn_primary w-100'
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editUser && (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1050,
          }}
        >
          <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
            <div className='modal-content bg-white' style={{ color: '#181C32' }}>
              <div className='modal-header'>
                <h5 className='modal-title'>Edit User - {editUser.username}</h5>
                <button type='button' className='btn-close' onClick={closeEditModal} />
              </div>
              <div className='modal-body'>
                <div className='row g-3'>
                  <div className='col-md-6'>
                    <label className='form-label fw-semibold'>Full Name</label>
                    <input
                      className='form-control'
                      value={editForm.fullName}
                      onChange={(e) => setEditForm((p) => ({ ...p, fullName: e.target.value }))}
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label fw-semibold'>Email</label>
                    <input
                      type='email'
                      className='form-control'
                      value={editForm.email}
                      onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label fw-semibold'>Mobile</label>
                    <input
                      className='form-control'
                      value={editForm.mobile}
                      onChange={(e) => setEditForm((p) => ({ ...p, mobile: e.target.value }))}
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label fw-semibold'>Date of Birth</label>
                    <input
                      type='date'
                      className='form-control'
                      value={editForm.dateOfBirth}
                      onChange={(e) => setEditForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
                    />
                  </div>
                  {operatorSubRolesUiEnabled &&
                    editUser &&
                    isOperatorBehaviorValue(editUser.roleBehaviorType, editUser.roleName) && (
                      <div className='col-md-6'>
                        <label className='form-label fw-semibold'>Operator Sub-Role</label>
                        <SearchableSelect
                          className='form-select'
                          value={editForm.operatorSubRoleId}
                          onChange={(e) =>
                            setEditForm((p) => ({ ...p, operatorSubRoleId: e.target.value }))
                          }
                          disabled={operatorSubRolesLoading}
                        >
                          <option value=''>-</option>
                          {operatorSubRoles.map((sr) => (
                            <option key={sr.id} value={sr.id}>
                              {sr.name}
                            </option>
                          ))}
                        </SearchableSelect>
                      </div>
                    )}
                  {editUser && isVesselAccessManagedRole(editUser) && (
                    <div className='col-12'>
                      <OperatorVesselAccessSection
                        accessMode={editVesselAccessMode}
                        onAccessModeChange={setEditVesselAccessMode}
                        selectedVesselIds={editAssignedVesselIds}
                        onSelectedVesselIdsChange={setEditAssignedVesselIds}
                        vessels={editAssignableVessels}
                        loading={editVesselAccessLoading}
                        vesselsLoading={editAssignableVesselsLoading}
                        saving={savingEdit || editVesselAccessSaving}
                        error={editVesselAccessError || editAssignableVesselsError}
                        disabled={savingEdit || editVesselAccessLoading}
                      />
                    </div>
                  )}
                  <div className='col-md-6 d-flex align-items-end'>
                    <div className='form-check'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={editForm.enabled}
                        onChange={(e) =>
                          setEditForm((p) => ({ ...p, enabled: e.target.checked }))
                        }
                        id='edit-user-enabled'
                      />
                      <label className='form-check-label' htmlFor='edit-user-enabled'>
                        Enabled
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-light' onClick={closeEditModal}>
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn_primary'
                  onClick={handleSaveEditUser}
                  disabled={savingEdit || editVesselAccessLoading || editAssignableVesselsLoading}
                >
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {resetUser && (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1050,
          }}
        >
          <div className='modal-dialog modal-dialog-centered' role='document'>
            <div className='modal-content bg-white text-dark'>
              <div className='modal-header text-dark'>
                <h5 className='modal-title text-dark'>Reset Password - {resetUser.username}</h5>
                <button type='button' className='btn-close' onClick={closeResetPasswordModal} />
              </div>
              <div className='modal-body text-dark'>
                {resetSuccessPassword ? (
                  <>
                    <div className='alert alert-success mb-3'>
                      Password reset successfully. Copy the temporary password below and share it securely with the user.
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                      <code className='flex-grow-1 p-2 bg-light rounded' style={{ wordBreak: 'break-all' }}>
                        {resetSuccessPassword}
                      </code>
                      <button
                        type='button'
                        className={`btn btn-sm ${resetPasswordCopied ? 'btn-success' : 'btn-light'}`}
                        onClick={() => copyResetPasswordToClipboard(resetSuccessPassword)}
                      >
                        {resetPasswordCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className='mb-3'>
                      <label className='form-label text-dark fw-semibold'>New Password</label>
                      <div className='input-group'>
                        <input
                          type={showResetPwd ? 'text' : 'password'}
                          className='form-control'
                          value={resetForm.newPassword}
                          onChange={(e) => {
                            setResetForm((p) => ({ ...p, newPassword: e.target.value }))
                            setResetSuccessPassword(null)
                          }}
                        />
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => setShowResetPwd((v) => !v)}
                        >
                          {showResetPwd ? 'Hide' : 'Show'}
                        </button>
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => copyResetPasswordToClipboard(resetForm.newPassword)}
                          disabled={!resetForm.newPassword}
                        >
                          {resetPasswordCopied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div className='mb-3'>
                      <label className='form-label text-dark fw-semibold'>Confirm Password</label>
                      <input
                        type={showResetPwd ? 'text' : 'password'}
                        className='form-control'
                        value={resetForm.confirmPassword}
                        onChange={(e) =>
                          setResetForm((p) => ({ ...p, confirmPassword: e.target.value }))
                        }
                      />
                    </div>
                    <button type='button' className='btn btn-light w-100 mb-3' onClick={handleGenerateResetPassword}>
                      Generate Password
                    </button>
                  </>
                )}
              </div>
              <div className='modal-footer'>
                {resetSuccessPassword ? (
                  <button type='button' className='btn btn_primary btn-sm' onClick={closeResetPasswordModal}>
                    Done
                  </button>
                ) : (
                  <>
                    <button type='button' className='btn btn-light' onClick={closeResetPasswordModal}>
                      Cancel
                    </button>
                    <button
                      type='button'
                      className='btn btn_primary'
                      onClick={handleResetPasswordSubmit}
                      disabled={resettingPassword}
                    >
                      {resettingPassword ? 'Saving...' : 'Reset Password'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className='modal-overlay'>
          <div
            className='modal-content'
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '115rem' }}
          >

            {/* Modal Header */}
            <div className='modal-header border-bottom' style={{ color: 'white', padding: '15px 20px' }}>
              <h5 className='modal-title fw-bold mb-0'>Assign Permissions For {selectedUser.name}</h5>
              <div className='d-flex align-items-center position-relative'>
                <input
                  type='text'
                  className='form-control form-control-sm me-3'
                  placeholder='Search modules, menus, submenus...'
                  value={permissionSearchTerm}
                  onChange={(e) => setPermissionSearchTerm(e.target.value)}
                  style={{ width: '300px', borderRadius: '20px' }}
                />

                {/* Search Results Dropdown */}
                {filteredResults.length > 0 && permissionSearchTerm && (
                  <div
                    className="position-absolute bg-white border rounded shadow-lg"
                    style={{
                      top: '100%',
                      left: '0',
                      right: '100px',
                      zIndex: 1060,
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}
                  >
                    {filteredResults.map((result, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 border-bottom"
                        style={{
                          fontSize: '14px',
                          cursor: 'pointer',
                          color: '#333'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        onClick={() => {
                          scrollToResult(result.key);
                          setPermissionSearchTerm('');
                        }}
                      >
                        <div>
                          <span className="badge bg-secondary me-2" style={{ fontSize: '10px' }}>
                            {result.type.toUpperCase()}
                          </span>
                          <span>
                            {result.type === 'module' && result.moduleName}
                            {result.type === 'menu' && `${result.moduleName} > ${result.menuName}`}
                            {result.type === 'submenu' && `${result.moduleName} > ${result.menuName} > ${result.submenuName}`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button className='close-btn' onClick={closePermissionsModal}>
                  <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className='modal-body p-4'>
              <div className='table-responsive' style={{ maxHeight: '65vh', overflowY: 'auto' }} ref={tableRef}>
                <table className='table table-bordered mb-0' style={{ fontSize: '14px' }}>

                  <thead style={{
                    backgroundColor: '#1C325B',
                    color: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <tr>
                      <th style={{ width: '60px', textAlign: 'center', padding: '12px 8px', fontWeight: 'bold' }}>SR/NO</th>
                      <th style={{ width: '200px', padding: '12px', fontWeight: 'bold' }}>Module Name</th>
                      <th style={{ width: '220px', padding: '12px', fontWeight: 'bold' }}>Menu Name</th>
                      <th style={{ width: '220px', padding: '12px', fontWeight: 'bold' }}>Sub Menu Name</th>
                      <th style={{ minWidth: '400px', padding: '12px', fontWeight: 'bold', textAlign: 'center' }}>Permission</th>
                    </tr>
                  </thead>

                  <tbody>
                    {menuLoading && (
                      <tr>
                        <td colSpan={5} className='text-center py-4'>
                          Loading permissions...
                        </td>
                      </tr>
                    )}
                    {!menuLoading && Object.keys(menuStructure)
                      .filter(moduleName => {
                        const moduleData = menuStructure[moduleName];
                        return moduleData.menus.some((menuName: string) => {
                          const submenus = moduleData.submenus[menuName] || [];
                          return shouldShowRow(moduleName, menuName) ||
                            submenus.some((submenuName: string) => shouldShowRow(moduleName, menuName, submenuName));
                        });
                      })
                      .map((moduleName, moduleIndex) => {
                        const moduleData = menuStructure[moduleName];
                        let moduleRows: JSX.Element[] = [];

                        const visibleMenus = moduleData.menus.filter((menuName: string) => {
                          const submenus = moduleData.submenus[menuName] || [];
                          return shouldShowRow(moduleName, menuName) ||
                            submenus.some((submenuName: string) => shouldShowRow(moduleName, menuName, submenuName));
                        });

                        const totalVisibleRows = visibleMenus.reduce((acc, menuName) => {
                          const submenus = moduleData.submenus[menuName] || [];
                          const visibleSubmenus = submenus.filter((submenuName: string) => shouldShowRow(moduleName, menuName, submenuName));

                          // If the parent menu matches the search but no submenus do, we still show the menu row.
                          if (shouldShowRow(moduleName, menuName) && visibleSubmenus.length === 0 && submenus.length > 0) {
                            return acc + 1;
                          }
                          return acc + Math.max(1, visibleSubmenus.length);
                        }, 0);

                        visibleMenus.forEach((menuName, menuIndex) => {
                          const hasSubmenus = moduleData.submenus[menuName]?.length > 0;
                          const menuKey = `${moduleName}-${menuName}`;

                          if (hasSubmenus) {
                            const visibleSubmenus = moduleData.submenus[menuName].filter((submenuName: string) =>
                              shouldShowRow(moduleName, menuName, submenuName)
                            );

                            if (visibleSubmenus.length === 0 && !shouldShowRow(moduleName, menuName)) {
                              return; // Don't render anything for this menu if no submenus match and menu itself doesn't match
                            }

                            visibleSubmenus.forEach((submenuName, submenuIndex) => {
                              const submenuKey = `${moduleName}-${menuName}-${submenuName}`;
                              const isFirstSubmenu = submenuIndex === 0;
                              const isFirstMenuOfModule = menuIndex === 0 && submenuIndex === 0;
                              const submenuCount = visibleSubmenus.length;

                              moduleRows.push(
                                <tr
                                  key={submenuKey}
                                  id={`row-${submenuKey}`}
                                  style={{
                                    borderTop: (menuIndex > 0 && isFirstSubmenu) ? '3px solid #dee2e6' : '1px solid #dee2e6',
                                    backgroundColor: submenuIndex % 2 === 0 ? '#fafafa' : '#ffffff'
                                  }}
                                >
                                  {isFirstMenuOfModule && (
                                    <td
                                      rowSpan={totalVisibleRows}
                                      style={{
                                        textAlign: 'center',
                                        verticalAlign: 'middle',
                                        fontWeight: 'bold',
                                        backgroundColor: '#f1f3f4',
                                        borderRight: '2px solid #dee2e6'
                                      }}
                                    >
                                      {moduleIndex + 1}
                                    </td>
                                  )}
                                  {isFirstMenuOfModule && (
                                    <td
                                      rowSpan={totalVisibleRows}
                                      style={{
                                        verticalAlign: 'middle',
                                        backgroundColor: '#E6EEF7',
                                        fontWeight: 'bold',
                                        borderRight: '2px solid #dee2e6',
                                        padding: '12px'
                                      }}
                                    >
                                      <div className='d-flex align-items-center'>
                                        <KTSVG path='/media/icons/duotune/general/gen019.svg' className='svg-icon-2 me-2 text-success' />
                                        <span style={{ color: '#2d5a2d' }}>{highlightText(moduleName, permissionSearchTerm)}</span>
                                      </div>
                                    </td>
                                  )}

                                  {isFirstSubmenu && (
                                    <td
                                      rowSpan={submenuCount}
                                      style={{
                                        verticalAlign: 'middle',
                                        backgroundColor: submenuIndex % 2 === 0 ? '#fafafa' : '#ffffff',
                                        borderRight: '2px solid #dee2e6',
                                        padding: '12px'
                                      }}
                                    >
                                      <div className='d-flex align-items-center'>
                                        <input
                                          type='checkbox'
                                          className='form-check-input me-2'
                                          style={{ transform: 'scale(1.2)' }}
                                          checked={selectedMenus.has(menuKey)}
                                          onChange={(e) => handleMenuSelection(moduleName, menuName, e.target.checked)}
                                        />
                                        <span style={{ color: '#1e3a8a' }}>{highlightText(menuName, permissionSearchTerm)}</span>
                                      </div>
                                    </td>
                                  )}

                                  <td style={{ paddingLeft: '20px', borderRight: '2px solid #dee2e6', padding: '12px' }}>
                                    <div className='d-flex align-items-center'>
                                      <input
                                        type='checkbox'
                                        className='form-check-input me-2'
                                        style={{ transform: 'scale(1.1)' }}
                                        checked={selectedSubmenus.has(submenuKey)}
                                        onChange={(e) => handleSubmenuSelection(moduleName, menuName, submenuName, e.target.checked)}
                                      />
                                      <span style={{ color: '#4b5563' }}>{highlightText(submenuName, permissionSearchTerm)}</span>
                                    </div>
                                  </td>

                                  <td style={{ padding: '12px' }}>
                                    <div className='d-flex align-items-center justify-content-center gap-4'>
                                      {(['view', 'add', 'edit', 'delete', 'approve', 'download'] as const).map((permission) => (
                                        <div key={permission} className='form-check form-check-inline'>
                                          <input
                                            type='checkbox'
                                            className='form-check-input'
                                            id={`${submenuKey}-${permission}`}
                                            disabled={!selectedSubmenus.has(submenuKey)}
                                            checked={permissions[submenuKey]?.[permission] || false}
                                            onChange={(e) =>
                                              handlePermissionChange(submenuKey, permission, e.target.checked)
                                            }
                                            style={{
                                              transform: 'scale(1.1)',
                                              accentColor: '#4ECDC4',
                                            }}
                                          />
                                          <label
                                            className='form-check-label text-capitalize fw-semibold'
                                            htmlFor={`${submenuKey}-${permission}`}
                                            style={{
                                              color: selectedSubmenus.has(submenuKey) ? '#059669' : '#9ca3af',
                                              fontSize: '12px',
                                            }}
                                          >
                                            Can {permission}
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              );
                            });
                          } else if (shouldShowRow(moduleName, menuName)) {

                            const isFirstMenuOfModule = menuIndex === 0;

                            moduleRows.push(
                              <tr
                                key={menuKey}
                                id={`row-${menuKey}`}
                                style={{
                                  borderTop: menuIndex > 0 ? '3px solid #dee2e6' : '1px solid #dee2e6',
                                  backgroundColor: menuIndex % 2 === 0 ? '#fafafa' : '#ffffff'
                                }}
                              >
                                {isFirstMenuOfModule && (
                                  <td
                                    rowSpan={totalVisibleRows}
                                    style={{
                                      textAlign: 'center',
                                      verticalAlign: 'middle',
                                      fontWeight: 'bold',
                                      backgroundColor: '#f1f3f4',
                                      borderRight: '2px solid #dee2e6'
                                    }}
                                  >
                                    {moduleIndex + 1}
                                  </td>
                                )}
                                {isFirstMenuOfModule && (
                                  <td
                                    rowSpan={totalVisibleRows}
                                    style={{
                                      verticalAlign: 'middle',
                                      backgroundColor: '#E6EEF7',
                                      fontWeight: 'bold',
                                      borderRight: '2px solid #dee2e6',
                                      padding: '12px'
                                    }}
                                  >
                                    <div className='d-flex align-items-center'>
                                      <KTSVG path='/media/icons/duotune/general/gen019.svg' className='svg-icon-2 me-2 text-success' />
                                      <span style={{ color: '#2d5a2d' }}>{highlightText(moduleName, permissionSearchTerm)}</span>
                                    </div>
                                  </td>
                                )}

                                <td style={{ borderRight: '2px solid #dee2e6', padding: '12px', backgroundColor: menuIndex % 2 === 0 ? '#fafafa' : '#ffffff', }}>
                                  <div className='d-flex align-items-center'>
                                    <input
                                      type='checkbox'
                                      className='form-check-input me-2'
                                      style={{ transform: 'scale(1.2)' }}
                                      checked={selectedMenus.has(menuKey)}
                                      onChange={(e) => handleMenuSelection(moduleName, menuName, e.target.checked)}
                                    />
                                    <span style={{ color: '#1e3a8a' }}>{highlightText(menuName, permissionSearchTerm)}</span>
                                  </div>
                                </td>

                                <td style={{
                                  backgroundColor: '#f9f9f9',
                                  borderRight: '2px solid #dee2e6',
                                  padding: '12px',
                                  textAlign: 'center',
                                  fontStyle: 'italic',
                                  color: '#9ca3af'
                                }}>

                                </td>

                                <td style={{ padding: '12px' }}>
                                  <div className='d-flex align-items-center justify-content-center gap-4'>
                                    {(['view', 'add', 'edit', 'delete', 'approve', 'download'] as const).map((permission) => (
                                      <div key={permission} className='form-check form-check-inline'>
                                        <input
                                          type='checkbox'
                                          className='form-check-input'
                                          id={`${menuKey}-${permission}`}
                                          disabled={!selectedMenus.has(menuKey)}
                                          checked={permissions[menuKey]?.[permission] || false}
                                          onChange={(e) =>
                                            handlePermissionChange(menuKey, permission, e.target.checked)
                                          }
                                          style={{
                                            transform: 'scale(1.1)',
                                            accentColor: '#4ECDC4',
                                          }}
                                        />
                                        <label
                                          className='form-check-label text-capitalize fw-semibold'
                                          htmlFor={`${menuKey}-${permission}`}
                                          style={{
                                            color: selectedMenus.has(menuKey) ? '#059669' : '#9ca3af',
                                            fontSize: '12px',
                                          }}
                                        >
                                          Can {permission}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                        });

                        return moduleRows;
                      }).flat()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div className='modal-footer border-top' style={{ padding: '15px 20px' }}>
              <button
                type='button'
                className='btn btn-secondary'
                onClick={closePermissionsModal}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary ms-2'
                onClick={savePermissions}
              >
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default UserManagement
